import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  FaBell,
  FaBolt,
  FaCheckCircle,
  FaEnvelope,
  FaImages,
  FaLightbulb,
  FaPenNib,
  FaRegCommentDots,
  FaRoute,
  FaShieldAlt,
  FaSignOutAlt,
  FaUserEdit,
} from "react-icons/fa";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ResizableImage } from "./ResizableImage";
import YoutubeExt from "@tiptap/extension-youtube";
import LinkExt from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import { TextStyle, Color, FontFamily, FontSize } from "@tiptap/extension-text-style";
import {
  collection, getDocs, addDoc, doc, setDoc, deleteDoc,
  query, orderBy, limit, serverTimestamp, where, onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  signInWithEmailAndPassword, signOut, onAuthStateChanged,
} from "firebase/auth";
import { db, storage, auth } from "../firebase/firebase-config";
import { logAction } from "../firebase/logAction";
import JourneyPanel from "./AdminJourney";
import HeroPanel from "./AdminHero";
import MediaPanel from "./AdminMedia";
import SkillsPanel from "./AdminSkills";
import AboutPanel from "./AdminAbout";
import IntroPanel from "./AdminIntro";
import NewsletterPanel from "./AdminNewsletter";
import "../Styles/Admin.css";
import PodcastStudio from "./AdminPodcast";
import GuidancePanel from "./AdminGuidance";
import SEO from "../components/SEO";
import AdminPPEdit from "./AdminPPEdit";

const ADMIN_EMAIL = "prachi.biswal1@gmail.com";
/* Always derive from current host — no hardcoded domain needed */
const SITE_URL = window.location.origin;

/* ── CHIME ── */
const playChime = () => {
  try {
    if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    [[523.25, 0], [659.25, 0.13], [783.99, 0.26], [1046.5, 0.4]].forEach(([freq, t]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      const at = ctx.currentTime + t;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.22, at + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, at + 0.75);
      osc.start(at); osc.stop(at + 0.75);
    });
  } catch (_) {}
};

/* ── CONFETTI ── */
const CONFETTI_COLORS = ["#c25b2a","#d4af37","#4f9cf9","#a78bfa","#34d399","#fb7185","#fbbf24"];
const Confetti = () => {
  const pieces = React.useMemo(() =>
    Array.from({ length: 70 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.9,
      dur: 1.4 + Math.random() * 0.8,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      size: 6 + Math.random() * 9,
      rotate: Math.random() * 360,
    })), []);
  return (
    <div className="adm-confetti" aria-hidden="true">
      {pieces.map(p => (
        <div key={p.id} className="adm-confetti-piece" style={{
          left: `${p.left}%`, width: p.size, height: p.size,
          background: p.color, borderRadius: p.id % 3 === 0 ? "50%" : "2px",
          animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`,
          transform: `rotate(${p.rotate}deg)`,
        }} />
      ))}
    </div>
  );
};

/* ── PUBLISH FLOW MODAL ── */
const PublishFlowModal = ({ form, section, slug, onPublish, onClose }) => {
  const [agreed, setAgreed] = useState(false);
  const [attribution, setAttribution] = useState("");
  const [done, setDone] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pubError, setPubError] = useState("");
  const [copied, setCopied] = useState(false);

  const path = section === "blogs" ? "blog" : "contract-tips";
  const postUrl = `${SITE_URL}/${path}/${slug}`;

  const checks = [
    { label: "Title", ok: !!form.title.trim(), required: true },
    { label: "Cover image", ok: !!form.coverImage, required: false },
    { label: section === "blogs" ? "Excerpt / subtitle" : "Subtitle / key takeaway", ok: !!form.subtitle?.trim(), required: false },
    { label: "Tags", ok: toArr(form.tags).length > 0, required: false },
    { label: "Author name", ok: !!form.authorName?.trim(), required: true },
  ];
  const canPublish = checks.filter(c => c.required).every(c => c.ok) && agreed;

  const handlePublish = async () => {
    setPublishing(true);
    setPubError("");
    try {
      await onPublish(attribution);
      setDone(true);
      playChime();
    } catch {
      setPubError("Publish failed — check Firestore write permissions.");
    } finally {
      setPublishing(false);
    }
  };

  const shareLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`, "_blank");
  const shareTwitter  = () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent(form.title)}`, "_blank");
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(form.title + "\n" + postUrl)}`, "_blank");
  const copyLink = () => {
    navigator.clipboard.writeText(postUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2200); });
  };

  /* ── CELEBRATION SCREEN ── */
  if (done) return (
    <div className="adm-overlay">
      <Confetti />
      <div className="adm-publish-done">
        <div className="adm-done-check">✓</div>
        <h2 className="adm-done-title">You're live! 🎉</h2>
        <p className="adm-done-sub">
          <strong>"{form.title}"</strong> is published and visible to everyone.
        </p>
        <a className="adm-done-url" href={postUrl} target="_blank" rel="noreferrer">{postUrl}</a>
        <p className="adm-done-share-label">Share it now</p>
        <div className="adm-share-btns">
          <button className="adm-share-btn li" onClick={shareLinkedIn}>
            <span>in</span> LinkedIn
          </button>
          <button className="adm-share-btn tw" onClick={shareTwitter}>
            <span>𝕏</span> Twitter / X
          </button>
          <button className="adm-share-btn wa" onClick={shareWhatsApp}>
            <span>💬</span> WhatsApp
          </button>
          <button className={`adm-share-btn cp${copied ? " copied" : ""}`} onClick={copyLink}>
            <span>{copied ? "✓" : "🔗"}</span> {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
        <button className="adm-done-back" onClick={onClose}>← Back to Dashboard</button>
      </div>
    </div>
  );

  /* ── REVIEW SCREEN ── */
  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-publish-modal" onClick={e => e.stopPropagation()}>

        <div className="adm-pf-header">
          <h2>Ready to publish?</h2>
          <button type="button" className="adm-pf-close" onClick={onClose}>✕</button>
        </div>

        <div className="adm-pf-body">

          {/* ── LEFT: OG preview + checklist ── */}
          <div className="adm-pf-left">
            <p className="adm-pf-label">How it looks when shared on LinkedIn &amp; apps</p>
            <div className="adm-og-card">
              {form.coverImage
                ? <div className="adm-og-img" style={{
                    backgroundImage: `url(${form.coverImage})`,
                    backgroundPosition: `${form.coverPositionX ?? 50}% ${form.coverPositionY ?? 50}%`,
                  }} />
                : <div className="adm-og-img adm-og-no-img">No cover image</div>
              }
              <div className="adm-og-body">
                <span className="adm-og-domain">{SITE_URL.replace(/https?:\/\//, '')}</span>
                <p className="adm-og-title">{form.title || "Your title here…"}</p>
                <p className="adm-og-desc">{form.subtitle || "Your excerpt will appear here when shared."}</p>
              </div>
            </div>

            <p className="adm-pf-label" style={{ marginTop: "1.4rem" }}>Pre-publish checklist</p>
            <ul className="adm-checklist">
              {checks.map(c => (
                <li key={c.label} className={`adm-check-item ${c.ok ? "ok" : c.required ? "err" : "warn"}`}>
                  <span className="adm-check-icon">{c.ok ? "✓" : c.required ? "✕" : "○"}</span>
                  <span className="adm-check-label">{c.label}</span>
                  {!c.ok && <span className="adm-check-hint">{c.required ? "required" : "recommended"}</span>}
                </li>
              ))}
            </ul>
          </div>

          {/* ── RIGHT: Author + attribution ── */}
          <div className="adm-pf-right">
            <p className="adm-pf-label">Author</p>
            <div className="adm-pf-author-card">
              {form.authorAvatar
                ? <img src={form.authorAvatar} alt={form.authorName} className="adm-pf-avatar" onError={e => e.target.style.display="none"} />
                : <div className="adm-pf-avatar-placeholder">{(form.authorName || "P")[0]}</div>
              }
              <div>
                <strong>{form.authorName || "—"}</strong>
                <span>{form.authorRole || "—"}</span>
                {form.authorBio && <em>{form.authorBio}</em>}
              </div>
            </div>

            <p className="adm-pf-label" style={{ marginTop: "1.4rem" }}>Image &amp; content attribution <span className="adm-pf-opt">optional</span></p>
            <p className="adm-pf-hint">If any images or quotes belong to someone else, credit them here. This saves with your post.</p>
            <textarea
              className="adm-pf-attribution"
              rows={3}
              placeholder={"Cover photo by Unsplash / @username\nQuote from [Book / Author]"}
              value={attribution}
              onChange={e => setAttribution(e.target.value)}
            />

            <label className="adm-pf-agree-row">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
              <span>I confirm this content is original or properly licensed, and I have the right to publish it.</span>
            </label>

            {pubError && <p className="adm-pf-err">{pubError}</p>}
          </div>
        </div>

        <div className="adm-pf-footer">
          <button type="button" className="adm-modal-cancel" onClick={onClose}>Not yet</button>
          <button
            type="button"
            className="adm-publish-go"
            onClick={handlePublish}
            disabled={!canPublish || publishing}
          >
            {publishing ? "Publishing…" : "✦ Publish now"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── UTILS ── */
const slugify = (str = "") =>
  str.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim()
    .replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 80);

const toArr = (str = "") =>
  Array.isArray(str)
    ? str.map((t) => String(t || "").trim().toLowerCase()).filter(Boolean)
    : String(str || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);

const tagsToString = (value) => toArr(value).join(", ");

const plainTextFromHtml = (html = "") =>
  String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const headingTextFromHtml = (html = "") => {
  const headings = [];
  String(html || "").replace(/<h[123][^>]*>([\s\S]*?)<\/h[123]>/gi, (_, text) => {
    const clean = plainTextFromHtml(text).slice(0, 120);
    if (clean) headings.push(clean);
    return "";
  });
  return headings.slice(0, 12);
};

const cleanSourceUrl = (value = "") => {
  const raw = String(value || "").trim();
  const markdownMatch = raw.match(/\((https?:\/\/[^)\s]+)\)/i);
  let url = markdownMatch ? markdownMatch[1] : raw;
  url = url.replace(/^["'<]+|[>"')]+$/g, "").trim();
  if (/^www\./i.test(url)) url = `https://${url}`;
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.toString() : "";
  } catch {
    return "";
  }
};

const sourceHost = (value = "") => {
  try {
    return new URL(cleanSourceUrl(value)).hostname.replace(/^www\./, "");
  } catch {
    return "Open source";
  }
};

const ytId = (url = "") =>
  url.match(/(?:v=|youtu\.be\/)([^&?\s]+)/)?.[1] || null;

/* ═══════════════════════════════════════════════
   LOGIN
═══════════════════════════════════════════════ */
const LoginScreen = () => {
  const [pw, setPw] = useState("");
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, ADMIN_EMAIL, pw);
    } catch (err) {
      setShake(true);
      setPw("");
      setError("Incorrect password. Try again.");
      setTimeout(() => setShake(false), 600);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adm-login">
      <div className={`adm-login-card${shake ? " shake" : ""}`}>
        <div className="adm-login-logo">✦</div>
        <h1>PP Studio</h1>
        <p>Admin Portal — enter your password to continue</p>
        <form onSubmit={submit}>
          <input
            className="adm-login-input adm-login-email"
            type="email"
            value={ADMIN_EMAIL}
            readOnly
            tabIndex={-1}
          />
          <input
            className="adm-login-input"
            type="password"
            placeholder="Password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            disabled={loading}
          />
          <button className="adm-login-btn" type="submit" disabled={loading}>
            {loading ? "Signing in…" : "Enter Studio →"}
          </button>
        </form>
        {error && <p className="adm-login-err">{error}</p>}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   AI TOPIC CARD  (tabbed, next-level)
═══════════════════════════════════════════════ */
const AI_CARD_TABS = [
  { id: "strategy",   label: "🎯 Strategy"   },
  { id: "sources",    label: "🔗 Sources"    },
  { id: "creative",   label: "🎨 Creative"   },
  { id: "distribute", label: "📣 Distribute" },
];

const AiTopicCard = ({ topic, index, priority, saved, saving, onSave, cleanSourceUrl, sourceHost }) => {
  const [tab, setTab] = React.useState("strategy");

  return (
    <article
      className={`adm-ai-card-v2 priority-${priority}`}
      style={{ "--card-index": index }}
    >
      {/* Card header */}
      <div className="adm-ai-card-header">
        <span className="adm-ai-card-rank">{index + 1}</span>
        <div className="adm-ai-card-meta">
          {topic.topic && <span className="adm-ai-card-category">{topic.topic}</span>}
          <h3 className="adm-ai-card-title">{topic.title}</h3>
        </div>
        <span className={`adm-ai-badge-priority priority-${priority}`}>
          {priority === "high" ? "🔴 High" : priority === "low" ? "🟢 Low" : "🟡 Med"}
        </span>
      </div>

      {/* Inner tabs */}
      <div className="adm-ai-card-tabs">
        {AI_CARD_TABS.map(t => (
          <button
            key={t.id}
            className={`adm-ai-card-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Strategy */}
      {tab === "strategy" && (
        <div className="adm-ai-card-body">
          {topic.rationale && (
            <div className="adm-ai-fact-row">
              <span className="adm-ai-fact-label">Why this topic</span>
              <p>{topic.rationale}</p>
            </div>
          )}
          {topic.why_now && (
            <div className="adm-ai-fact-row">
              <span className="adm-ai-fact-label">Why now</span>
              <p>{topic.why_now}</p>
            </div>
          )}
          {topic.target_reader && (
            <div className="adm-ai-fact-row">
              <span className="adm-ai-fact-label">Target reader</span>
              <p>{topic.target_reader}</p>
            </div>
          )}
          {topic.angle && (
            <div className="adm-ai-fact-row">
              <span className="adm-ai-fact-label">Angle</span>
              <p>{topic.angle}</p>
            </div>
          )}
          {topic.outline?.length > 0 && (
            <div className="adm-ai-fact-row">
              <span className="adm-ai-fact-label">Outline</span>
              <ol className="adm-ai-outline">
                {topic.outline.map((pt, i) => <li key={i}>{pt}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* Tab: Sources */}
      {tab === "sources" && (
        <div className="adm-ai-card-body">
          {topic.related_sources?.length > 0 ? (
            <div className="adm-ai-sources-v2">
              {topic.related_sources.map((src, i) => {
                const href = cleanSourceUrl(src.url);
                return href ? (
                  <a key={`${href}-${i}`} href={href} target="_blank" rel="noreferrer" className="adm-ai-source-card">
                    <span className="adm-ai-source-host">{sourceHost(href)}</span>
                    <strong>{src.title || "Source"}</strong>
                    {src.summary && <p>{src.summary}</p>}
                  </a>
                ) : (
                  <div key={`${src.title}-${i}`} className="adm-ai-source-card adm-ai-source-nohref">
                    <strong>{src.title || "Source"}</strong>
                    {src.summary && <p>{src.summary}</p>}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="adm-ai-card-empty">No sources attached for this idea.</p>
          )}
        </div>
      )}

      {/* Tab: Creative */}
      {tab === "creative" && (
        <div className="adm-ai-card-body">
          <div className="adm-ai-thumb-v2">
            <div className="adm-ai-thumb-mock">
              <span className="adm-ai-thumb-brand-tag">The PP Edit</span>
              <strong>{topic.thumbnail?.headline_text || topic.title}</strong>
              {topic.thumbnail?.subtext && <span>{topic.thumbnail.subtext}</span>}
            </div>
            <div className="adm-ai-thumb-brief">
              {topic.thumbnail?.concept && (
                <div className="adm-ai-fact-row">
                  <span className="adm-ai-fact-label">Concept</span>
                  <p>{topic.thumbnail.concept}</p>
                </div>
              )}
              {topic.thumbnail?.visual_style && (
                <div className="adm-ai-fact-row">
                  <span className="adm-ai-fact-label">Style</span>
                  <p>{topic.thumbnail.visual_style}</p>
                </div>
              )}
              {topic.thumbnail?.layout_notes && (
                <div className="adm-ai-fact-row">
                  <span className="adm-ai-fact-label">Layout</span>
                  <p>{topic.thumbnail.layout_notes}</p>
                </div>
              )}
            </div>
          </div>
          {topic.thumbnail?.recommended_colours?.length > 0 && (
            <div className="adm-ai-colours-v2">
              {topic.thumbnail.recommended_colours.map(c => (
                <span key={c} className="adm-ai-colour-chip">{c}</span>
              ))}
            </div>
          )}
          {topic.thumbnail?.image_prompt && (
            <div className="adm-ai-prompt-v2">
              <span>Image prompt</span>
              <p>{topic.thumbnail.image_prompt}</p>
            </div>
          )}
        </div>
      )}

      {/* Tab: Distribute */}
      {tab === "distribute" && (
        <div className="adm-ai-card-body">
          {topic.social?.short_hook && (
            <div className="adm-ai-fact-row">
              <span className="adm-ai-fact-label">Hook</span>
              <p className="adm-ai-hook">{topic.social.short_hook}</p>
            </div>
          )}
          {topic.social?.linkedin_caption && (
            <div className="adm-ai-fact-row">
              <span className="adm-ai-fact-label">LinkedIn</span>
              <p>{topic.social.linkedin_caption}</p>
            </div>
          )}
          {topic.social?.instagram_caption && (
            <div className="adm-ai-fact-row">
              <span className="adm-ai-fact-label">Instagram</span>
              <p>{topic.social.instagram_caption}</p>
            </div>
          )}
          {topic.social?.hashtags?.length > 0 && (
            <div className="adm-ai-tags-v2">
              {topic.social.hashtags.map(tag => (
                <span key={tag}>{tag.startsWith("#") ? tag : `#${tag}`}</span>
              ))}
            </div>
          )}
          {topic.seo && (
            <div className="adm-ai-seo-box">
              <span className="adm-ai-fact-label">SEO</span>
              {topic.seo.meta_title && <p><b>Title:</b> {topic.seo.meta_title}</p>}
              {topic.seo.meta_description && <p><b>Description:</b> {topic.seo.meta_description}</p>}
              {topic.seo.keywords?.length > 0 && (
                <div className="adm-ai-tags-v2">
                  {topic.seo.keywords.map(k => <span key={k}>{k}</span>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Save footer */}
      <div className="adm-ai-card-footer">
        <button
          type="button"
          className={`adm-ai-save-v2 ${saved ? "saved" : ""}`}
          onClick={onSave}
          disabled={saved || saving}
        >
          {saved ? "✓ Saved to library" : saving ? "Saving…" : "Save to library"}
        </button>
      </div>
    </article>
  );
};

/* ═══════════════════════════════════════════════
   DASHBOARD
═══════════════════════════════════════════════ */
const Dashboard = ({ onNew, onEdit, onLogout }) => {
  const [tips, setTips] = useState([]);
  const [posts, setPosts] = useState([]);
  const [messages, setMessages]           = useState([]);
  const [auditLog, setAuditLog]           = useState([]);
  const [pendingComments, setPendingComments] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [deleting, setDeleting]           = useState(null);
  const [deletingMsg, setDeletingMsg]     = useState(null);
  const [permError, setPermError]         = useState(false);
  const [expandedMsg, setExpandedMsg]     = useState(null);
  const [expandedCmt, setExpandedCmt]     = useState(null);
  const [activeView, setActiveView]       = useState("overview");
  const [activeSitePanel, setActiveSitePanel] = useState("intro");
  const [contentFilter, setContentFilter] = useState("all");
  const [aiTopics,         setAiTopics]         = useState([]);
  const [aiLoading,        setAiLoading]        = useState(false);
  const [aiLoadStep,       setAiLoadStep]       = useState(0);
  const [aiError,          setAiError]          = useState("");
  const [aiMeta,           setAiMeta]           = useState(null);
  const [aiPanelTab,       setAiPanelTab]       = useState("chat");
  const [savedIdeas,       setSavedIdeas]       = useState([]);
  const [savedIdeasLoaded, setSavedIdeasLoaded] = useState(false);
  const [savingSuggestion, setSavingSuggestion] = useState("");
  const [savedSuggestions, setSavedSuggestions] = useState({});
  const [chatMessages,     setChatMessages]     = useState([{ role: "assistant", content: "Hi Pooja! I'm Naga — I know your full content library, what's performing, and what the gaps are. Ask me anything: what to write next, how to angle a topic, who to target, or how to promote it." }]);
  const [chatMode,         setChatMode]         = useState("studio"); // "studio" | "edit"
  const [editChatMessages, setEditChatMessages] = useState([{ role: "assistant", content: "Hey, it's me — switching hats. No content calendar, no SEO. Just tell me: what's actually been on your mind this week? A client thing, a feeling, something someone said that stuck." }]);
  const [chatInput,        setChatInput]        = useState("");
  const [chatLoading,      setChatLoading]      = useState(false);
  const [chatError,        setChatError]        = useState("");
  const [ppEditIdeas,        setPpEditIdeas]        = useState([]);
  const [ppEditIdeasLoading, setPpEditIdeasLoading] = useState(false);
  const [ppEditDraftingIdx,  setPpEditDraftingIdx]  = useState(null);
  const [expandedSavedId,  setExpandedSavedId]  = useState(null);
  const [draftingIdeaId,   setDraftingIdeaId]   = useState(null);
  const chatEndRef = useRef(null);
  const greetedRef = useRef(false);
  const aiLoadTimer = useRef(null);

  const AI_LOAD_STEPS = [
    "Naga is reading your content signals…",
    "Scanning current legal & business news…",
    "Analysing trending topics…",
    "Naga is thinking…",
    "Polishing editorial ideas…",
  ];

  useEffect(() => {
    if (aiLoading) {
      setAiLoadStep(0);
      aiLoadTimer.current = setInterval(() => {
        setAiLoadStep(s => (s + 1) % AI_LOAD_STEPS.length);
      }, 1800);
    } else {
      clearInterval(aiLoadTimer.current);
    }
    return () => clearInterval(aiLoadTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiLoading]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tSnap, bSnap, mSnap] = await Promise.all([
        getDocs(query(collection(db, "contract_tips"), orderBy("importance", "asc"))).catch((e) => { if (e.code === "permission-denied") setPermError(true); return { docs: [] }; }),
        getDocs(query(collection(db, "blogs"), orderBy("createdAt", "desc"))).catch((e) => { if (e.code === "permission-denied") setPermError(true); return { docs: [] }; }),
        getDocs(query(collection(db, "contactMessages"), orderBy("createdAt", "desc"))).catch(() => ({ docs: [] })),
      ]);
      setTips(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setPosts(bSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMessages(mSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      /* Fetch pending comments from every blog and tip directly — no index needed */
      const commentFetches = [
        ...bSnap.docs.map(d =>
          getDocs(query(collection(db, "blogs", d.id, "comments"), where("approved", "==", false)))
            .then(s => s.docs.map(c => ({ id: c.id, postSlug: d.id, postCollection: "blogs", ...c.data(), _ref: c.ref })))
            .catch(() => [])
        ),
        ...tSnap.docs.map(d =>
          getDocs(query(collection(db, "contract_tips", d.id, "comments"), where("approved", "==", false)))
            .then(s => s.docs.map(c => ({ id: c.id, postSlug: d.id, postCollection: "contract_tips", ...c.data(), _ref: c.ref })))
            .catch(() => [])
        ),
      ];
      const allComments = (await Promise.all(commentFetches)).flat();
      allComments.sort((a, b) => {
        const at = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const bt = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return bt - at;
      });
      setPendingComments(allComments);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = query(collection(db, "admin_logs"), orderBy("timestamp", "desc"), limit(50));
    const unsub = onSnapshot(q, snap => {
      setAuditLog(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
    return unsub;
  }, []);

  const handleDelete = async (section, id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await deleteDoc(doc(db, section, id));
      logAction(`${section}.delete`, title, id);
      await load();
    }
    catch (e) { alert("Delete failed — check Firestore write permissions."); }
    finally { setDeleting(null); }
  };

  const handleMarkRead = async (id) => {
    try {
      await setDoc(doc(db, "contactMessages", id), { read: true }, { merge: true });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
    } catch {}
  };

  const handleDeleteMsg = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    setDeletingMsg(id);
    try { await deleteDoc(doc(db, "contactMessages", id)); setMessages(prev => prev.filter(m => m.id !== id)); }
    catch { alert("Delete failed."); }
    finally { setDeletingMsg(null); }
  };

  const fmtDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const unread = messages.filter(m => !m.read).length;
  const totalContent = tips.length + posts.length;
  const pendingTotal = unread + pendingComments.length;
  const latestMessage = messages[0];
  const notificationText = pendingTotal > 0
    ? `Hi Pooja, you have ${pendingTotal} new ${pendingTotal === 1 ? "thing" : "things"} waiting. ${unread ? `${unread} message${unread === 1 ? "" : "s"}` : ""}${unread && pendingComments.length ? " and " : ""}${pendingComments.length ? `${pendingComments.length} comment${pendingComments.length === 1 ? "" : "s"} to review` : ""}.`
    : "Hi Pooja, everything is calm. No new comments or messages right now.";
  const contentItems = [
    ...posts.map(item => ({ ...item, _section: "blogs", _label: "Blog" })),
    ...tips.map(item => ({ ...item, _section: "contract_tips", _label: "Tip" })),
  ];
  const publishedContent = contentItems.filter(item => !item.draft);
  const draftsInProgress = contentItems.filter(item => item.draft);
  const getContentDate = (item) => {
    const raw = item.createdAt || item.publishedAt || item.updatedAt;
    if (!raw) return null;
    const d = raw.toDate ? raw.toDate() : new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const contentScore = (item) => (item.views || 0) + (item.likes || 0) * 3 + (item.commentCount || 0) * 10;
  const performanceInsights = useMemo(() => {
    const liveBlogs = posts.filter(p => !p.draft);
    const liveTips = tips.filter(t => !t.draft);
    const avg = list => list.length
      ? list.reduce((sum, item) => sum + contentScore(item), 0) / list.length
      : 0;
    const blogAvg = avg(liveBlogs);
    const tipAvg = avg(liveTips);
    const all = [...liveBlogs.map(i => ({ ...i, _label: "Blog" })), ...liveTips.map(i => ({ ...i, _label: "Tip" }))];
    const top = [...all].sort((a, b) => contentScore(b) - contentScore(a))[0];
    const recent = [...all]
      .sort((a, b) => (getContentDate(b)?.getTime() || 0) - (getContentDate(a)?.getTime() || 0))
      .slice(0, 3);
    const recentQuiet = recent.length === 3 && recent.every(item => (item.commentCount || 0) === 0);
    const lines = [];
    if (liveBlogs.length && liveTips.length) {
      const stronger = tipAvg >= blogAvg ? "Contract tips" : "Blog posts";
      const weaker = tipAvg >= blogAvg ? "blog posts" : "contract tips";
      const ratio = Math.max(tipAvg, blogAvg) / Math.max(Math.min(tipAvg, blogAvg), 1);
      lines.push({
        title: `${stronger} are leading`,
        text: `${stronger} are getting ${ratio.toFixed(1)}x more engagement than ${weaker} on average, using views and likes as the main signal.`,
        action: stronger === "Contract tips"
          ? "Turn the best tips into deeper blog posts, carousels, and short LinkedIn explainers."
          : "Pull the best blog ideas into shorter contract-tip formats for faster reading.",
      });
    }
    if (top?.title) {
      lines.push({
        title: "Double down on the winner",
        text: `"${top.title}" is your strongest performer with ${(top.views || 0).toLocaleString()} views and ${top.likes || 0} likes.`,
        action: "Create a follow-up, a practical checklist, or a client scenario version of this topic.",
      });
    }
    if (recentQuiet) {
      lines.push({
        title: "Recent comments are quiet",
        text: "Your last 3 published pieces have no comments yet.",
        action: "End the next post with a sharper question, a simple poll-style prompt, or a practical reader scenario.",
      });
    }
    const latestDate = recent[0] ? getContentDate(recent[0]) : null;
    if (latestDate) {
      const days = Math.floor((Date.now() - latestDate.getTime()) / 86400000);
      if (days >= 10) {
        lines.push({
          title: "Publishing rhythm needs attention",
          text: `Your last published content was ${days} days ago.`,
          action: "Schedule one quick contract tip this week, then one deeper article next week.",
        });
      }
    }
    if (!lines.length) {
      lines.push({
        title: "Start collecting signals",
        text: "There is not enough engagement data yet to compare patterns confidently.",
        action: "Publish consistently for 3-4 weeks, then use this panel to decide what to double down on.",
      });
    }
    return lines;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, tips]);
  const calendarItems = useMemo(() => {
    const published = publishedContent
      .map(item => ({ type: "published", label: item._label, title: item.title || "Untitled", date: getContentDate(item), item }))
      .filter(item => item.date)
      .sort((a, b) => b.date - a.date)
      .slice(0, 10);
    const drafts = draftsInProgress
      .slice(0, 5)
      .map(item => ({ type: "draft", label: item._label, title: item.title || "Untitled draft", date: getContentDate(item), item }));
    const ideas = savedIdeas
      .slice(0, 5)
      .map(item => ({ type: "idea", label: "Idea", title: item.title || "Saved idea", date: item.createdAt?.toDate ? item.createdAt.toDate() : null, item }));
    return [...drafts, ...ideas, ...published].slice(0, 18);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedContent, draftsInProgress, savedIdeas]);
  const bestPostDays = useMemo(() => {
    const dayStats = new Map();
    publishedContent.forEach(item => {
      const d = getContentDate(item);
      if (!d) return;
      const day = d.toLocaleDateString("en-GB", { weekday: "long" });
      const stat = dayStats.get(day) || { day, score: 0, count: 0 };
      stat.score += contentScore(item);
      stat.count += 1;
      dayStats.set(day, stat);
    });
    const ranked = [...dayStats.values()]
      .map(stat => ({ ...stat, avg: stat.score / Math.max(stat.count, 1) }))
      .sort((a, b) => b.avg - a.avg);
    return ranked.length ? ranked.slice(0, 2) : [{ day: "Tuesday", avg: 0 }, { day: "Thursday", avg: 0 }];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedContent]);
  const filteredContent = contentItems.filter(item =>
    contentFilter === "all" ||
    (contentFilter === "blogs" && item._section === "blogs") ||
    (contentFilter === "tips" && item._section === "contract_tips")
  );
  const popularContent = [...filteredContent]
    .sort((a, b) => ((b.views || 0) - (a.views || 0)) || ((b.likes || 0) - (a.likes || 0)))
    .slice(0, 6);
  const sitePanels = [
    {
      key: "hero",
      label: "Homepage hero",
      detail: "Hero, contact photos and credentials",
      icon: <FaCheckCircle />,
      tone: "copper",
    },
    {
      key: "intro",
      label: "Home intro",
      detail: "Get to Know Me cards",
      icon: <FaUserEdit />,
      tone: "copper",
    },
    {
      key: "about",
      label: "About page",
      detail: "Full profile content",
      icon: <FaUserEdit />,
      tone: "gold",
    },
    {
      key: "skills",
      label: "Skills",
      detail: "Skills and achievements",
      icon: <FaBolt />,
      tone: "blue",
    },
    {
      key: "journey",
      label: "Journey",
      detail: "Bus, train and flight story",
      icon: <FaRoute />,
      tone: "green",
    },
    {
      key: "media",
      label: "Media",
      detail: "Images used across pages",
      icon: <FaImages />,
      tone: "pink",
    },
    {
      key: "newsletter",
      label: "Newsletter",
      detail: "Write an issue and send it",
      icon: <FaBolt />,
      tone: "gold",
    },
  ];

  const generateTopicIdeas = async (fromChat = false) => {
    setAiLoading(true);
    setAiPanelTab("generate");
    setAiError("");
    setAiMeta(null);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Please sign in again before using AI suggestions.");
      const token = await currentUser.getIdToken();
      const body = fromChat && chatMessages.length > 1
        ? { chatContext: chatMessages.slice(-20).map(m => `${m.role === "user" ? "Pooja" : "Naga"}: ${m.content}`).join("\n") }
        : {};
      const res = await fetch("/api/ai/topic-suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not generate topic ideas.");
      setAiTopics(Array.isArray(data.suggestions) ? data.suggestions : []);
      setAiMeta(data.analysed || null);
    } catch (err) {
      setAiError(err.message || "Could not generate topic ideas.");
    } finally {
      setAiLoading(false);
    }
  };

  const saveTopicSuggestion = async (topic) => {
    const key = topic.title || `${Date.now()}`;
    setSavingSuggestion(key);
    try {
      await addDoc(collection(db, "aiTopicSuggestions"), {
        topic: topic.topic || "",
        title: topic.title || "",
        rationale: topic.rationale || "",
        why_now: topic.why_now || "",
        target_reader: topic.target_reader || "",
        angle: topic.angle || "",
        outline: Array.isArray(topic.outline) ? topic.outline : [],
        related_sources: Array.isArray(topic.related_sources) ? topic.related_sources : [],
        priority: topic.priority || "Medium",
        thumbnail: topic.thumbnail || {},
        social: topic.social || {},
        seo: topic.seo || {},
        createdAt: serverTimestamp(),
        status: "suggested",
        source: "groq_tavily",
      });
      setSavedSuggestions(prev => ({ ...prev, [key]: true }));
      playChime();
    } catch (err) {
      alert("Save failed — check Firestore permissions.");
    } finally {
      setSavingSuggestion("");
    }
  };

  const loadSavedIdeas = async () => {
    if (savedIdeasLoaded) return;
    try {
      const snap = await getDocs(query(collection(db, "aiTopicSuggestions"), orderBy("createdAt", "desc"), limit(20)));
      setSavedIdeas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSavedIdeasLoaded(true);
    } catch(e) { console.error(e); }
  };

  useEffect(() => {
    if ((activeView === "calendar" || activeView === "insights") && !savedIdeasLoaded) {
      loadSavedIdeas();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, savedIdeasLoaded]);

  const deleteSavedIdea = async (id) => {
    if (!window.confirm("Remove this saved idea?")) return;
    await deleteDoc(doc(db, "aiTopicSuggestions", id));
    setSavedIdeas(prev => prev.filter(i => i.id !== id));
  };

  const writeIdeaToEditor = async (section, idea) => {
    const key = `${idea.id}-${section}`;
    setDraftingIdeaId(key);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch("/api/ai/draft", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ idea, type: "outline" }),
      });
      const data = await res.json().catch(() => ({}));
      onNew(section, {
        title: idea.title,
        subtitle: idea.rationale?.slice(0, 200) || "",
        tags: tagsToString(idea.seo?.keywords).split(", ").slice(0, 5).join(", "),
        category: idea.topic || "",
        content: data.content || "",
      });
    } catch {
      onNew(section, {
        title: idea.title,
        subtitle: idea.rationale?.slice(0, 200) || "",
        tags: tagsToString(idea.seo?.keywords).split(", ").slice(0, 5).join(", "),
        category: idea.topic || "",
      });
    } finally {
      setDraftingIdeaId(null);
    }
  };

  const draftIntroForIdea = async (idea) => {
    setAiPanelTab("chat");
    const userMsg = { role: "user", content: `Write an intro paragraph for my article: "${idea.title}"` };
    const history = [...chatMessages, userMsg];
    setChatMessages(history);
    setChatLoading(true);
    setChatError("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch("/api/ai/draft", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ idea, type: "intro" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Draft failed.");
      setChatMessages(prev => [...prev, { role: "assistant", content: data.content, copyable: true }]);
    } catch (err) {
      setChatError(err.message || "Something went wrong.");
    } finally {
      setChatLoading(false);
    }
  };

  const sendChatMessage = async (e) => {
    e?.preventDefault();
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    const isEditMode = chatMode === "edit";
    const setMessages = isEditMode ? setEditChatMessages : setChatMessages;
    const currentMessages = isEditMode ? editChatMessages : chatMessages;
    const userMsg = { role: "user", content: text };
    const history = [...currentMessages, userMsg];
    setMessages(history);
    setChatInput("");
    setChatLoading(true);
    setChatError("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(isEditMode ? "/api/ai/pp-edit-muse" : "/api/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ history: history.slice(-20) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Chat request failed.");
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setChatError(err.message || "Something went wrong.");
    } finally {
      setChatLoading(false);
    }
  };

  const generatePPEditIdeasFromChat = async () => {
    setPpEditIdeasLoading(true);
    setChatError("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch("/api/ai/pp-edit-ideas", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ history: editChatMessages.slice(-20) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not generate ideas from this chat.");
      setPpEditIdeas(Array.isArray(data.ideas) ? data.ideas : []);
      if (!data.ideas || data.ideas.length === 0) {
        setChatError("Nothing concrete enough yet — keep talking and try again once something real comes up.");
      }
    } catch (err) {
      setChatError(err.message || "Something went wrong.");
    } finally {
      setPpEditIdeasLoading(false);
    }
  };

  const draftPPEditIdea = async (idea, index) => {
    setPpEditDraftingIdx(index);
    setChatError("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch("/api/ai/pp-edit-draft", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ idea }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not draft this post.");
      setEditChatMessages(prev => [...prev, {
        role: "assistant",
        content: `Here's a first draft of "${idea.title}":\n\n${data.draft}`,
        copyable: true,
      }]);
      setPpEditIdeas(prev => prev.filter((_, i) => i !== index));
    } catch (err) {
      setChatError(err.message || "Something went wrong.");
    } finally {
      setPpEditDraftingIdx(null);
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, editChatMessages, chatLoading]);

  useEffect(() => {
    if (loading) return;
    if (posts.length === 0 && tips.length === 0) return;
    setChatMessages(prev => {
      if (prev.length > 1) return prev;
      const allContent = [
        ...posts.filter(p => !p.draft).map(p => ({ ...p, _type: "blog" })),
        ...tips.filter(t => !t.draft).map(t => ({ ...t, _type: "tip" })),
      ];
      if (allContent.length === 0) return prev;
      const lines = [];
      const pubPosts = posts.filter(p => !p.draft);
      const pubTips = tips.filter(t => !t.draft);
      lines.push(`Good to see you, Pooja. Here's your content snapshot:\n`);
      lines.push(`You have ${pubPosts.length} blog post${pubPosts.length !== 1 ? "s" : ""} and ${pubTips.length} contract tip${pubTips.length !== 1 ? "s" : ""} published.\n`);
      const sorted = [...allContent].sort((a, b) =>
        ((b.views || 0) + (b.likes || 0) * 3) - ((a.views || 0) + (a.likes || 0) * 3)
      );
      if (sorted[0]?.title) {
        lines.push(`Your top performer is "${sorted[0].title}" with ${sorted[0].views || 0} views and ${sorted[0].likes || 0} likes.\n`);
      }
      if (pubPosts.length > 0 && pubTips.length > 0) {
        const avgBlog = pubPosts.reduce((s, p) => s + (p.views || 0) + (p.likes || 0) * 3, 0) / pubPosts.length;
        const avgTip = pubTips.reduce((s, t) => s + (t.views || 0) + (t.likes || 0) * 3, 0) / pubTips.length;
        const ratio = Math.round(Math.max(avgTip, avgBlog) / Math.max(Math.min(avgTip, avgBlog), 1) * 10) / 10;
        if (avgTip > avgBlog * 1.2) lines.push(`Contract tips are getting ${ratio}x more engagement than blog posts on average.\n`);
        else if (avgBlog > avgTip * 1.2) lines.push(`Blog posts are getting ${ratio}x more engagement than contract tips on average.\n`);
        else lines.push(`Blog posts and contract tips are performing at a similar level right now.\n`);
      }
      const dates = allContent
        .map(c => c.createdAt?.toDate ? c.createdAt.toDate() : c.createdAt ? new Date(c.createdAt) : null)
        .filter(Boolean).sort((a, b) => b - a);
      if (dates[0]) {
        const days = Math.floor((Date.now() - dates[0].getTime()) / 86400000);
        if (days > 14) lines.push(`You haven't published in ${days} days — your audience might be wondering where you are.\n`);
        else if (days > 7) lines.push(`Last published ${days} days ago. A new post soon would keep the momentum going.\n`);
        else lines.push(`Last published ${days === 0 ? "today" : `${days} day${days !== 1 ? "s" : ""} ago`} — great consistency.\n`);
      }
      const allTags = allContent.flatMap(c => [...toArr(c.tags), c.category || ""].map(t => (t || "").toLowerCase().trim())).filter(Boolean);
      const COMMON_TOPICS = ["freelance", "nda", "payment", "termination", "privacy", "liability", "negotiation", "employment", "invoice", "startup"];
      const gaps = COMMON_TOPICS.filter(t => !allTags.some(tag => tag.includes(t)));
      if (gaps.length > 0) lines.push(`Content gaps I noticed: nothing yet on ${gaps.slice(0, 3).join(", ")} — popular topics for your audience.\n`);
      lines.push(`\nAnything here you want to dig into? Just ask.`);
      return [...prev, { role: "assistant", content: lines.join(""), isBrief: true }];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, posts, tips]);

  useEffect(() => {
    if (loading || greetedRef.current) return;
    greetedRef.current = true;
    if (pendingTotal > 0) {
      window.setTimeout(playChime, 450);
    }
  }, [loading, pendingTotal]);

  const handleApproveComment = async (cmt) => {
    try {
      await setDoc(cmt._ref, { approved: true }, { merge: true });
      setPendingComments(prev => prev.filter(c => c.id !== cmt.id));
      logAction("comment.approve", `"${cmt.name}" on ${cmt.postTitle || "a post"}`, cmt.id);
    } catch { alert("Approve failed."); }
  };

  const handleDeleteComment = async (cmt) => {
    if (!window.confirm(`Delete this comment by "${cmt.name}"?`)) return;
    try {
      await deleteDoc(cmt._ref);
      setPendingComments(prev => prev.filter(c => c.id !== cmt.id));
    } catch { alert("Delete failed."); }
  };

  const PostRow = ({ item, section }) => {
    const mins = item.content
      ? Math.max(1, Math.ceil(item.content.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length / 200))
      : null;
    return (
      <div className="adm-row">
        <div
          className="adm-row-thumb"
          style={{
            backgroundImage: `url(${item.thumbnail || item.coverImage || ""})`,
            background: item.thumbnail || item.coverImage
              ? `url(${item.thumbnail || item.coverImage}) center/cover`
              : "linear-gradient(135deg,#f0ebe4,#ede5dc)",
          }}
        >
          {!item.thumbnail && !item.coverImage && (
            <span className="adm-row-thumb-icon">📄</span>
          )}
        </div>
        <div className="adm-row-info">
          <div className="adm-row-badges">
            <span className={`adm-badge ${section}`}>
              {section === "contract_tips" ? "⚖ Contract Tip" : "✍ Blog Post"}
            </span>
            {item.featured && <span className="adm-badge featured">★ Featured</span>}
            {item.draft && <span className="adm-badge draft">Draft</span>}
          </div>
          <h4 className="adm-row-title">{item.title || "Untitled"}</h4>
          <p className="adm-row-meta">
            {item.category && <span>{item.category}</span>}
            {toArr(item.tags).slice(0, 3).map((t) => <span key={t}>#{t}</span>)}
          </p>
          <div className="adm-row-analytics">
            {mins && <span title="Est. reading time">⏱ {mins} min</span>}
            {item.views > 0 && <span title="Views">👁 {(item.views || 0).toLocaleString()}</span>}
            {item.likes > 0 && <span title="Likes">♥ {item.likes || 0}</span>}
          </div>
        </div>
        <div className="adm-row-actions">
          <button className="adm-btn-edit" onClick={() => onEdit(section, item)}>
            ✏ Edit
          </button>
          <button
            className="adm-btn-del"
            onClick={() => handleDelete(section, item.id, item.title)}
            disabled={deleting === item.id}
          >
            {deleting === item.id ? "…" : "🗑"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="adm-dash">
      <header className="adm-dash-top">
        <div className="adm-dash-brand">
          <span className="adm-dash-logo">✦</span>
          <span>PP Studio</span>
          <em>Control room</em>
        </div>
        <div className="adm-dash-acts">
          <button className="adm-new-tip" onClick={() => onNew("contract_tips")}>
            <FaLightbulb /> Contract Tip
          </button>
          <button className="adm-new-blog" onClick={() => onNew("blogs")}>
            <FaPenNib /> Blog Post
          </button>
          <button className="adm-logout" onClick={onLogout}><FaSignOutAlt /> Sign out</button>
        </div>
      </header>

      {permError && (
        <div className="adm-perm-banner">
          <strong>⚠ Permission denied.</strong>
          <span>
            Your session may have expired. Try signing out and back in.
          </span>
          <button type="button" onClick={() => setPermError(false)}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="adm-dash-loading">
          <div className="adm-spinner" />
          <p>Loading your content…</p>
        </div>
      ) : (
        <div className={`adm-dash-body view-${activeView}`}>
          <section className="adm-command-center">
            <div className="adm-hello-card">
              <div className="adm-hello-copy">
                <span className="adm-kicker"><FaBolt /> Live dashboard</span>
                <h1>Hi Pooja, your studio is ready.</h1>
                <p>{notificationText}</p>
              </div>
              <div className={`adm-live-orb${pendingTotal > 0 ? " hot" : ""}`}>
                <FaBell />
                {pendingTotal > 0 && <strong>{pendingTotal}</strong>}
              </div>
            </div>

            <div className="adm-notice-card">
              <div className="adm-notice-icon">
                {pendingComments.length > 0 ? <FaRegCommentDots /> : unread > 0 ? <FaEnvelope /> : <FaCheckCircle />}
              </div>
              <div>
                <strong>
                  {pendingComments.length > 0
                    ? `You got ${pendingComments.length} comment${pendingComments.length === 1 ? "" : "s"} to approve`
                    : unread > 0
                      ? `You got ${unread} new message${unread === 1 ? "" : "s"}`
                      : "All clear for now"}
                </strong>
                <span>
                  {latestMessage
                    ? `Latest: ${latestMessage.name || "Someone"} wrote ${fmtDate(latestMessage.createdAt)}`
                    : "No inbox activity yet. New leads will show here first."}
                </span>
              </div>
            </div>

            <div className="adm-smart-stats">
              <div><FaPenNib /><strong>{posts.length}</strong><span>Blog posts</span></div>
              <div><FaLightbulb /><strong>{tips.length}</strong><span>Contract tips</span></div>
              <div><FaEnvelope /><strong>{unread}</strong><span>Unread leads</span></div>
              <div><FaRegCommentDots /><strong>{pendingComments.length}</strong><span>Comments</span></div>
            </div>

            <div className="adm-quick-grid">
              <button onClick={() => onNew("blogs")}><FaPenNib /><span>Write blog</span></button>
              <button onClick={() => onNew("contract_tips")}><FaLightbulb /><span>Add tip</span></button>
              <button
                className="adm-quick-ai"
                onClick={() => setActiveView("overview")}
              >
                <FaBolt /><span>AskNaga</span>
              </button>
              <button onClick={() => { setActiveView("site"); setActiveSitePanel("media"); }}><FaImages /><span>Media</span></button>
              <button onClick={() => { setActiveView("site"); setActiveSitePanel("about"); }}><FaUserEdit /><span>About</span></button>
              <button onClick={() => { setActiveView("site"); setActiveSitePanel("journey"); }}><FaRoute /><span>Journey</span></button>
              <button onClick={() => setActiveView("inbox")}><FaBell /><span>Inbox</span></button>
            </div>
          </section>

          <section className="adm-section-map" aria-label="Admin sections">
            {[
              ["overview", "AskNaga", null],
              ["insights", "Insights", performanceInsights.length],
              ["calendar", "Calendar", calendarItems.length],
              ["popular", "Popular", totalContent],
              ["ppedit", "PP Edit", posts.length],
              ["podcast", "Podcast", null],
              ["guidance", "Guidance", null],
              ["site", "Site pages", null],
              ["content", "Content", totalContent],
              ["inbox", "Notifications", pendingTotal],
              ["audit", "Audit", auditLog.length || null],
            ].map(([view, label, count]) => (
              <button
                key={view}
                type="button"
                className={activeView === view ? "active" : ""}
                onClick={() => setActiveView(view)}
              >
                {label}
                {count !== null && <strong>{count}</strong>}
              </button>
            ))}
          </section>

          <section className="adm-panel-shell adm-view-panel adm-view-ppedit"><AdminPPEdit /></section>

          <section className="adm-site-switcher adm-view-panel adm-view-site">
            <div className="adm-site-switcher-head">
              <div>
                <span className="adm-kicker"><FaImages /> Site pages</span>
                <h2>Pick one area to edit</h2>
                <p>No more huge mixed page. Choose the section, edit it, then switch when needed.</p>
              </div>
            </div>
            <div className="adm-site-cards">
              {sitePanels.map(panel => (
                <button
                  key={panel.key}
                  type="button"
                  className={`adm-site-card ${panel.tone} ${activeSitePanel === panel.key ? "active" : ""}`}
                  onClick={() => setActiveSitePanel(panel.key)}
                >
                  <span className="adm-site-card-icon">{panel.icon}</span>
                  <span className="adm-site-card-copy">
                    <strong>{panel.label}</strong>
                    <em>{panel.detail}</em>
                  </span>
                  <span className="adm-site-card-arrow">›</span>
                </button>
              ))}
            </div>
          </section>

          <section id="admin-ai" className="adm-panel adm-ai-panel adm-view-panel adm-view-overview">

            {/* ── HERO HEADER ── */}
            <div className="adm-ai-hero">
              <div className="adm-ai-hero-left">
                <div className="adm-ai-eyebrow">
                  <span className="adm-ai-eyebrow-dot" />
                  <FaBolt /> AskNaga
                </div>
                <h2 className="adm-ai-hero-title">Ask Naga anything</h2>
                <p className="adm-ai-hero-sub">
                  Naga knows your full content library, audience signals, and today's legal news — ask what to write, how to angle it, or generate 5 ready-to-go ideas.
                </p>
                <div className="adm-ai-hero-pills">
                  <span>💬 Chat with context</span>
                  <span>📊 Audience signals</span>
                  <span>📰 Live news scan</span>
                  <span>🎯 SEO strategy</span>
                  <span>🎨 Thumbnail brief</span>
                </div>
              </div>
              <div className="adm-ai-hero-right">
                <button
                  type="button"
                  className={`adm-ai-generate-v2 ${aiLoading ? "loading" : ""}`}
                  onClick={generateTopicIdeas}
                  disabled={aiLoading}
                >
                  <span className="adm-ai-gen-inner">
                    <FaBolt className="adm-ai-gen-bolt" />
                    <span>{aiLoading ? "Working…" : "Generate Ideas"}</span>
                  </span>
                  {!aiLoading && <span className="adm-ai-gen-shine" />}
                </button>
              </div>
            </div>

            {/* ── PANEL TABS ── */}
            <div className="adm-ai-nav">
              <button className={`adm-ai-nav-btn ${aiPanelTab === "chat" ? "active" : ""}`}
                onClick={() => setAiPanelTab("chat")}>
                💬 Chat
              </button>
              <button className={`adm-ai-nav-btn ${aiPanelTab === "generate" ? "active" : ""}`}
                onClick={() => setAiPanelTab("generate")}>
                ✦ Ideas
                {aiTopics.length > 0 && <span className="adm-ai-nav-count">{aiTopics.length}</span>}
              </button>
              <button className={`adm-ai-nav-btn ${aiPanelTab === "signals" ? "active" : ""}`}
                onClick={() => setAiPanelTab("signals")}
                disabled={!aiMeta}>
                📡 Signals
              </button>
              <button className={`adm-ai-nav-btn ${aiPanelTab === "saved" ? "active" : ""}`}
                onClick={() => { setAiPanelTab("saved"); loadSavedIdeas(); }}>
                🗂 Saved
                {savedIdeas.length > 0 && <span className="adm-ai-nav-count">{savedIdeas.length}</span>}
              </button>
            </div>

            {aiError && (
              <div className="adm-ai-error-v2">
                <span>⚠</span> {aiError}
                <button onClick={() => setAiError("")}>✕</button>
              </div>
            )}

            {/* ── CHAT TAB ── */}
            {aiPanelTab === "chat" && (
              <div className="adm-ai-chat">
                <div className="adm-ai-chat-mode-toggle">
                  <button
                    type="button"
                    className={`adm-ai-mode-btn${chatMode === "studio" ? " active" : ""}`}
                    onClick={() => setChatMode("studio")}
                  >
                    🎯 PP Studio
                  </button>
                  <button
                    type="button"
                    className={`adm-ai-mode-btn${chatMode === "edit" ? " active" : ""}`}
                    onClick={() => setChatMode("edit")}
                  >
                    🌸 PP Edit
                  </button>
                </div>
                {chatMode === "edit" && (
                  <p className="adm-ai-mode-sub">
                    Not strategy — just a conversation. Naga asks about your life and pitches a post the moment something real comes up.
                  </p>
                )}
                <div className="adm-ai-chat-messages">
                  {(chatMode === "edit" ? editChatMessages : chatMessages).map((msg, i) => (
                    <div key={i} className={`adm-ai-chat-bubble ${msg.role}${msg.isBrief ? " brief" : ""}`}>
                      <span className="adm-ai-chat-name">{msg.role === "assistant" ? "Naga" : "Pooja"}</span>
                      {msg.role === "assistant" && <span className="adm-ai-chat-avatar">{chatMode === "edit" ? "🌸" : (msg.isBrief ? "📊" : "✦")}</span>}
                      <div className="adm-ai-chat-text">{msg.content}</div>
                      {msg.copyable && (
                        <button
                          className="adm-ai-chat-copy"
                          onClick={() => navigator.clipboard.writeText(msg.content).then(() => {})}
                          title="Copy to clipboard"
                        >
                          📋 Copy
                        </button>
                      )}
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="adm-ai-chat-bubble assistant">
                      <span className="adm-ai-chat-name">Naga</span>
                      <span className="adm-ai-chat-avatar">{chatMode === "edit" ? "🌸" : "✦"}</span>
                      <div className="adm-ai-chat-typing">
                        <span /><span /><span />
                      </div>
                    </div>
                  )}
                  {chatError && (
                    <div className="adm-ai-chat-error">{chatError}</div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                {chatMode === "edit" && editChatMessages.length === 1 && !chatLoading && (
                  <div className="adm-ai-starter-chips">
                    {[
                      "A client conversation that surprised me recently…",
                      "Something I believed at 22 that I don't anymore…",
                      "A small failure that taught me more than a win…",
                      "A pattern I keep noticing in people…",
                    ].map(prompt => (
                      <button
                        key={prompt}
                        type="button"
                        className="adm-ai-starter-chip"
                        onClick={() => setChatInput(prompt)}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}
                {chatMode === "studio" && chatMessages.length > 2 && !chatLoading && (
                  <div className="adm-ai-chat-actions">
                    <button
                      className="adm-ai-chat-gen-btn"
                      onClick={() => generateTopicIdeas(true)}
                      disabled={aiLoading}
                    >
                      <FaBolt /> Generate ideas from this chat
                    </button>
                  </div>
                )}
                {chatMode === "edit" && editChatMessages.length > 2 && !chatLoading && (
                  <div className="adm-ai-chat-actions">
                    <button
                      className="adm-ai-chat-gen-btn"
                      onClick={generatePPEditIdeasFromChat}
                      disabled={ppEditIdeasLoading}
                    >
                      <FaBolt /> {ppEditIdeasLoading ? "Reading back through it…" : "Turn this chat into post ideas"}
                    </button>
                  </div>
                )}
                {chatMode === "edit" && ppEditIdeas.length > 0 && (
                  <div className="adm-ai-edit-idea-list">
                    {ppEditIdeas.map((idea, index) => (
                      <div className="adm-ai-edit-idea-card" key={`${idea.title}-${index}`}>
                        <div className="adm-ai-edit-idea-title">{idea.title}</div>
                        <div className="adm-ai-edit-idea-hook">"{idea.hook}"</div>
                        <div className="adm-ai-edit-idea-why"><strong>Why it lands:</strong> {idea.why_it_lands}</div>
                        {idea.source_moment && (
                          <div className="adm-ai-edit-idea-source">From what you said: {idea.source_moment}</div>
                        )}
                        <button
                          className="adm-ai-edit-idea-draft-btn"
                          onClick={() => draftPPEditIdea(idea, index)}
                          disabled={ppEditDraftingIdx === index}
                        >
                          {ppEditDraftingIdx === index ? "Writing…" : "✍️ Draft this"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <form className="adm-ai-chat-input-row" onSubmit={sendChatMessage}>
                  <input
                    className="adm-ai-chat-input"
                    type="text"
                    placeholder={chatMode === "edit" ? "Tell Naga what's on your mind…" : "Ask Naga anything…"}
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    disabled={chatLoading}
                    autoComplete="off"
                  />
                  <button className="adm-ai-chat-send" type="submit" disabled={chatLoading || !chatInput.trim()}>
                    <FaBolt />
                  </button>
                </form>
              </div>
            )}

            {/* ── LOADING ── */}
            {aiLoading && (
              <div className="adm-ai-loading-v2">
                <div className="adm-ai-loader-orb">
                  <div className="adm-ai-orb-ring" />
                  <div className="adm-ai-orb-ring adm-ai-orb-ring-2" />
                  <FaBolt className="adm-ai-orb-icon" />
                </div>
                <div className="adm-ai-load-steps">
                  {AI_LOAD_STEPS.map((step, i) => (
                    <div key={i} className={`adm-ai-load-step ${i === aiLoadStep ? "active" : i < aiLoadStep ? "done" : ""}`}>
                      <span className="adm-ai-step-dot" />
                      {step}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── IDEAS TAB ── */}
            {!aiLoading && aiPanelTab === "generate" && (
              <>
                {aiTopics.length === 0 && !aiError && (
                  <div className="adm-ai-empty-v2">
                    <div className="adm-ai-empty-orb">✦</div>
                    <p>Hit "Generate Ideas" to get 5 editorial topics backed by real audience data and live news.</p>
                  </div>
                )}
                {aiTopics.length > 0 && (
                  <div className="adm-ai-topic-grid-v2">
                    {aiTopics.map((topic, index) => {
                      const saved = savedSuggestions[topic.title];
                      const priority = (topic.priority || "medium").toLowerCase();
                      return (
                        <AiTopicCard
                          key={`${topic.title}-${index}`}
                          topic={topic}
                          index={index}
                          priority={priority}
                          saved={saved}
                          saving={savingSuggestion === topic.title}
                          onSave={() => saveTopicSuggestion(topic)}
                          cleanSourceUrl={cleanSourceUrl}
                          sourceHost={sourceHost}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ── SIGNALS TAB ── */}
            {!aiLoading && aiPanelTab === "signals" && aiMeta && (
              <div className="adm-ai-signals-v2">
                <div className="adm-ai-signals-stats">
                  <div className="adm-ai-stat-pill">
                    <strong>{aiMeta.contentCount || 0}</strong>
                    <span>Posts analysed</span>
                  </div>
                  <div className="adm-ai-stat-pill">
                    <strong>{aiMeta.sourceCount || 0}</strong>
                    <span>News sources</span>
                  </div>
                  <div className="adm-ai-stat-pill">
                    <strong>{(aiMeta.topTags || []).length}</strong>
                    <span>Top tags</span>
                  </div>
                </div>
                {(aiMeta.topTags || []).length > 0 && (
                  <div className="adm-ai-tags-section">
                    <strong>Top content tags</strong>
                    <div className="adm-ai-tags">
                      {aiMeta.topTags.slice(0, 8).map(tag => <span key={tag}>#{tag}</span>)}
                    </div>
                  </div>
                )}
                {aiMeta.topicSignals?.length > 0 && (
                  <div className="adm-ai-signal-grid">
                    <strong>Audience interest signals</strong>
                    <div className="adm-ai-signal-list-v2">
                      {aiMeta.topicSignals.slice(0, 6).map((signal, i) => (
                        <div key={signal.topic} className="adm-ai-signal-card-v2" style={{ "--si": i }}>
                          <div className="adm-ai-signal-num">{i + 1}</div>
                          <div>
                            <strong>{signal.topic}</strong>
                            <p>{signal.rationale}</p>
                            {signal.examples?.length > 0 && (
                              <em>Seen in: {signal.examples.slice(0, 2).join(", ")}</em>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── SAVED TAB ── */}
            {!aiLoading && aiPanelTab === "saved" && (
              <div className="adm-ai-saved-v2">
                {savedIdeas.length === 0 && savedIdeasLoaded && (
                  <div className="adm-ai-empty-v2">
                    <div className="adm-ai-empty-orb">🗂</div>
                    <p>Generate ideas and click "Save to library" to build your editorial backlog.</p>
                  </div>
                )}
                {!savedIdeasLoaded && (
                  <div className="adm-ai-empty-v2">
                    <div className="adm-spinner" style={{ margin: "0 auto" }} />
                  </div>
                )}
                <div className="adm-ai-saved-list">
                  {savedIdeas.map(idea => {
                    const isOpen = expandedSavedId === idea.id;
                    const priority = (idea.priority || "medium").toLowerCase();
                    return (
                      <div key={idea.id} className={`adm-ai-saved-row ${isOpen ? "open" : ""}`}>
                        <div className="adm-ai-saved-header" onClick={() => setExpandedSavedId(isOpen ? null : idea.id)}>
                          <div className="adm-ai-saved-info">
                            <span className={`adm-ai-priority-dot priority-${priority}`} />
                            <div>
                              <strong>{idea.title}</strong>
                              <span>{idea.topic}</span>
                            </div>
                          </div>
                          <div className="adm-ai-saved-actions">
                            <span className="adm-ai-saved-date">
                              {idea.createdAt?.toDate ? idea.createdAt.toDate().toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : "—"}
                            </span>
                            <span className="adm-ai-saved-chevron">{isOpen ? "▲" : "▼"}</span>
                          </div>
                        </div>
                        {isOpen && (
                          <div className="adm-ai-saved-detail">
                            {idea.rationale && (
                              <div className="adm-ai-fact-row">
                                <span className="adm-ai-fact-label">Why this</span>
                                <p>{idea.rationale}</p>
                              </div>
                            )}
                            {idea.angle && (
                              <div className="adm-ai-fact-row">
                                <span className="adm-ai-fact-label">Angle</span>
                                <p>{idea.angle}</p>
                              </div>
                            )}
                            {idea.outline?.length > 0 && (
                              <div className="adm-ai-fact-row">
                                <span className="adm-ai-fact-label">Outline</span>
                                <ol className="adm-ai-outline">
                                  {idea.outline.map((pt, i) => <li key={i}>{pt}</li>)}
                                </ol>
                              </div>
                            )}
                            {idea.social?.short_hook && (
                              <div className="adm-ai-fact-row">
                                <span className="adm-ai-fact-label">Hook</span>
                                <p>{idea.social.short_hook}</p>
                              </div>
                            )}
                            {idea.seo?.keywords?.length > 0 && (
                              <div className="adm-ai-tags-v2">
                                {idea.seo.keywords.slice(0, 6).map(k => <span key={k}>{k}</span>)}
                              </div>
                            )}
                            <div className="adm-ai-saved-write-row">
                              <button
                                className="adm-ai-write-btn"
                                disabled={draftingIdeaId === `${idea.id}-blogs`}
                                onClick={() => writeIdeaToEditor("blogs", idea)}
                              >
                                {draftingIdeaId === `${idea.id}-blogs`
                                  ? <><span className="adm-ai-write-spin" /> Naga is drafting…</>
                                  : <><FaPenNib /> Write as Blog Post</>}
                              </button>
                              <button
                                className="adm-ai-write-btn adm-ai-write-btn-tip"
                                disabled={draftingIdeaId === `${idea.id}-contract_tips`}
                                onClick={() => writeIdeaToEditor("contract_tips", idea)}
                              >
                                {draftingIdeaId === `${idea.id}-contract_tips`
                                  ? <><span className="adm-ai-write-spin" /> Naga is drafting…</>
                                  : <><FaLightbulb /> Write as Contract Tip</>}
                              </button>
                              <button
                                className="adm-ai-write-btn adm-ai-write-btn-intro"
                                onClick={() => draftIntroForIdea(idea)}
                              >
                                ✍️ Draft intro paragraph
                              </button>
                              <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => deleteSavedIdea(idea.id)}>Delete</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </section>

          <section className="adm-panel adm-view-panel adm-view-insights">
            <div className="adm-panel-head">
              <div>
                <h2>Performance insights</h2>
                <p>Naga turns views, likes, comments and publishing rhythm into plain English next steps</p>
              </div>
              <span className="adm-panel-count">{performanceInsights.length} insights</span>
            </div>
            <div className="adm-insight-grid">
              {performanceInsights.map((insight, index) => (
                <article key={insight.title} className="adm-insight-card">
                  <span className="adm-insight-num">{index + 1}</span>
                  <div>
                    <h3>{insight.title}</h3>
                    <p>{insight.text}</p>
                    <strong>{insight.action}</strong>
                  </div>
                </article>
              ))}
            </div>
            <div className="adm-insight-summary">
              <div>
                <span>Blog avg</span>
                <strong>
                  {posts.filter(p => !p.draft).length
                    ? Math.round(posts.filter(p => !p.draft).reduce((s, p) => s + contentScore(p), 0) / posts.filter(p => !p.draft).length)
                    : 0}
                </strong>
              </div>
              <div>
                <span>Tip avg</span>
                <strong>
                  {tips.filter(t => !t.draft).length
                    ? Math.round(tips.filter(t => !t.draft).reduce((s, t) => s + contentScore(t), 0) / tips.filter(t => !t.draft).length)
                    : 0}
                </strong>
              </div>
              <div>
                <span>Drafts waiting</span>
                <strong>{draftsInProgress.length}</strong>
              </div>
              <div>
                <span>Saved ideas</span>
                <strong>{savedIdeas.length}</strong>
              </div>
            </div>
          </section>

          <section className="adm-panel adm-view-panel adm-view-calendar">
            <div className="adm-panel-head">
              <div>
                <h2>Content calendar</h2>
                <p>Published dates, drafts, saved ideas and suggested next posting days in one place</p>
              </div>
              <span className="adm-panel-count">{calendarItems.length} items</span>
            </div>
            <div className="adm-calendar-advice">
              <div>
                <span>Best days to try next</span>
                <strong>{bestPostDays.map(d => d.day).join(" + ")}</strong>
                <p>
                  {bestPostDays[0]?.avg
                    ? "Based on your current engagement patterns by publish day."
                    : "Default rhythm until more engagement data builds up."}
                </p>
              </div>
              <button type="button" onClick={() => { setActiveView("overview"); setAiPanelTab("saved"); loadSavedIdeas(); }}>
                Open saved ideas
              </button>
            </div>
            {calendarItems.length === 0 ? (
              <div className="adm-empty">
                <span>📅</span>
                <p>No calendar items yet. Publish posts or save AI ideas and they will appear here.</p>
              </div>
            ) : (
              <div className="adm-calendar-list">
                {calendarItems.map((item, index) => (
                  <button
                    key={`${item.type}-${item.item.id || index}`}
                    type="button"
                    className={`adm-calendar-item ${item.type}`}
                    onClick={() => {
                      if (item.type === "published" || item.type === "draft") onEdit(item.item._section, item.item);
                      if (item.type === "idea") { setActiveView("overview"); setAiPanelTab("saved"); setExpandedSavedId(item.item.id); loadSavedIdeas(); }
                    }}
                  >
                    <span className="adm-calendar-date">
                      {item.date
                        ? item.date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                        : "Next"}
                    </span>
                    <span className="adm-calendar-copy">
                      <strong>{item.title}</strong>
                      <em>{item.type === "published" ? `Published ${item.label}` : item.type === "draft" ? `${item.label} draft in progress` : "Saved idea ready to turn into a draft"}</em>
                    </span>
                    <span className="adm-calendar-status">{item.type}</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="adm-panel adm-view-panel adm-view-popular">
            <div className="adm-panel-head">
              <div>
                <h2>Popular content</h2>
                <p>Highest viewed posts and tips, so you see what is working first</p>
              </div>
              <div className="adm-filter-pills">
                {[
                  ["all", "All"],
                  ["blogs", "Blogs"],
                  ["tips", "Tips"],
                ].map(([v, label]) => (
                  <button key={v} className={contentFilter === v ? "active" : ""} onClick={() => setContentFilter(v)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {popularContent.length === 0 ? (
              <div className="adm-empty">
                <span>📈</span>
                <p>No view data yet. Popular posts will appear here after readers visit.</p>
              </div>
            ) : (
              <div className="adm-popular-list">
                {popularContent.map((item, index) => (
                  <button
                    key={`${item._section}-${item.id}`}
                    className="adm-popular-item"
                    onClick={() => onEdit(item._section, item)}
                  >
                    <span className="adm-pop-rank">{index + 1}</span>
                    <span className="adm-pop-body">
                      <strong>{item.title || "Untitled"}</strong>
                      <em>{item._label}</em>
                    </span>
                    <span className="adm-pop-metric">{(item.views || 0).toLocaleString()} views</span>
                    <span className="adm-pop-metric muted">{item.likes || 0} likes</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Media Library */}
          <div id="admin-hero" className={`adm-panel-shell adm-site-editor adm-view-panel adm-view-site ${activeSitePanel === "hero" ? "active" : ""}`}><HeroPanel /></div>
          <div id="admin-media" className={`adm-panel-shell adm-site-editor adm-view-panel adm-view-site ${activeSitePanel === "media" ? "active" : ""}`}><MediaPanel /></div>

          {/* Newsletter composer */}
          <div id="admin-newsletter" className={`adm-panel-shell adm-site-editor adm-view-panel adm-view-site ${activeSitePanel === "newsletter" ? "active" : ""}`}><NewsletterPanel /></div>

          {/* Homepage Intro — Get to Know Me */}
          <div id="admin-site" className={`adm-panel-shell adm-site-editor adm-view-panel adm-view-site ${activeSitePanel === "intro" ? "active" : ""}`}><IntroPanel /></div>

          {/* /know-me page */}
          <div id="admin-about" className={`adm-panel-shell adm-site-editor adm-view-panel adm-view-site ${activeSitePanel === "about" ? "active" : ""}`}><AboutPanel /></div>

          {/* Skills & Achievements */}
          <div id="admin-skills" className={`adm-panel-shell adm-site-editor adm-view-panel adm-view-site ${activeSitePanel === "skills" ? "active" : ""}`}><SkillsPanel /></div>

          {/* Journey Phases */}
          <div id="admin-journey" className={`adm-panel-shell adm-site-editor adm-view-panel adm-view-site ${activeSitePanel === "journey" ? "active" : ""}`}><JourneyPanel /></div>

          {/* Podcast Studio */}
          <section id="admin-podcast" className="pod-panel-wrap adm-view-panel adm-view-podcast">
            <PodcastStudio />
          </section>

          {/* Guidance Sessions */}
          <section id="admin-guidance" className="adm-panel-shell adm-view-panel adm-view-guidance">
            <GuidancePanel />
          </section>

          {/* Contract Tips */}
          <section id="admin-content" className="adm-panel adm-view-panel adm-view-content">
            <div className="adm-panel-head">
              <div>
                <h2>⚖ Contract Management Tips</h2>
                <p>Practical advice Pooja publishes on contracts &amp; freelancing</p>
              </div>
              <span className="adm-panel-count">{tips.length} tips</span>
            </div>
            {tips.length === 0 ? (
              <div className="adm-empty">
                <span>📝</span>
                <p>No contract tips yet.</p>
                <button onClick={() => onNew("contract_tips")}>Write your first tip →</button>
              </div>
            ) : (
              <div className="adm-rows">{tips.map((t) => <PostRow key={t.id} item={t} section="contract_tips" />)}</div>
            )}
          </section>

          {/* Blog */}
          <section className="adm-panel adm-view-panel adm-view-content">
            <div className="adm-panel-head">
              <div>
                <h2>✍ The PP Edit — Blog</h2>
                <p>Personal essays, reflections, and professional insights</p>
              </div>
              <span className="adm-panel-count">{posts.length} posts</span>
            </div>
            {posts.length === 0 ? (
              <div className="adm-empty">
                <span>✍</span>
                <p>No blog posts yet.</p>
                <button onClick={() => onNew("blogs")}>Write your first post →</button>
              </div>
            ) : (
              <div className="adm-rows">{posts.map((p) => <PostRow key={p.id} item={p} section="blogs" />)}</div>
            )}
          </section>

          {/* Pending Comments */}
          <section id="admin-inbox" className="adm-panel adm-view-panel adm-view-inbox">
            <div className="adm-panel-head">
              <div>
                <h2>
                  💬 Pending Comments
                  {pendingComments.length > 0 && <span className="adm-msg-badge">{pendingComments.length} awaiting</span>}
                </h2>
                <p>Reader comments waiting for your approval before going public</p>
              </div>
              <span className="adm-panel-count">{pendingComments.length} pending</span>
            </div>
            {pendingComments.length === 0 ? (
              <div className="adm-empty">
                <span>✅</span>
                <p>No pending comments — all caught up!</p>
              </div>
            ) : (
              <div className="adm-msg-list">
                {pendingComments.map(c => (
                  <div key={c.id} className="adm-msg-card">
                    <div className="adm-msg-header" onClick={() => setExpandedCmt(expandedCmt === c.id ? null : c.id)}>
                      <div className="adm-msg-who">
                        <span className="adm-msg-avatar">{(c.name || "?")[0].toUpperCase()}</span>
                        <div>
                          <strong className="adm-msg-name">{c.name}</strong>
                          <span className="adm-msg-email" style={{ color: '#888' }}>
                            {c.postCollection === 'contract_tips' ? '⚖ Tip' : '✍ Blog'}: {c.postSlug}
                          </span>
                        </div>
                        <span className="adm-msg-dot" title="Pending approval" style={{ background: '#D4AF37' }} />
                      </div>
                      <div className="adm-msg-meta">
                        <time className="adm-msg-time">{fmtDate(c.createdAt)}</time>
                        <button
                          className="adm-btn-edit"
                          style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}
                          onClick={e => { e.stopPropagation(); handleApproveComment(c); }}
                          title="Approve & publish"
                        >
                          ✓ Approve
                        </button>
                        <button
                          className="adm-btn-del"
                          onClick={e => { e.stopPropagation(); handleDeleteComment(c); }}
                          title="Delete comment"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                    {expandedCmt === c.id && (
                      <div className="adm-msg-body">
                        <p>{c.text}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Contact Messages */}
          <section className="adm-panel adm-view-panel adm-view-inbox">
            <div className="adm-panel-head">
              <div>
                <h2>
                  📬 Contact Messages
                  {unread > 0 && <span className="adm-msg-badge">{unread} new</span>}
                </h2>
                <p>Messages sent through the portfolio contact form</p>
              </div>
              <span className="adm-panel-count">{messages.length} total</span>
            </div>
            {messages.length === 0 ? (
              <div className="adm-empty">
                <span>📭</span>
                <p>No messages yet. They'll appear here when someone contacts you.</p>
              </div>
            ) : (
              <div className="adm-msg-list">
                {messages.map(m => (
                  <div key={m.id} className={`adm-msg-card${m.read ? " adm-msg-read" : ""}`}>
                    <div className="adm-msg-header" onClick={() => {
                      setExpandedMsg(expandedMsg === m.id ? null : m.id);
                      if (!m.read) handleMarkRead(m.id);
                    }}>
                      <div className="adm-msg-who">
                        <span className="adm-msg-avatar">{(m.name || "?")[0].toUpperCase()}</span>
                        <div>
                          <strong className="adm-msg-name">{m.name}</strong>
                          <a href={`mailto:${m.email}`} className="adm-msg-email" onClick={e => e.stopPropagation()}>{m.email}</a>
                        </div>
                        {!m.read && <span className="adm-msg-dot" title="Unread" />}
                      </div>
                      <div className="adm-msg-meta">
                        <time className="adm-msg-time">{fmtDate(m.createdAt)}</time>
                        <button
                          className="adm-btn-del"
                          onClick={e => { e.stopPropagation(); handleDeleteMsg(m.id); }}
                          disabled={deletingMsg === m.id}
                          title="Delete message"
                        >
                          {deletingMsg === m.id ? "…" : "🗑"}
                        </button>
                      </div>
                    </div>
                    {expandedMsg === m.id && (
                      <div className="adm-msg-body">
                        <p>{m.message}</p>
                        <a href={`mailto:${m.email}?subject=Re: Your message to Pooja Biswal`} className="adm-msg-reply">
                          ↩ Reply via email
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── AUDIT TERMINAL ── */}
          <section className="adm-panel adm-view-panel adm-view-audit">
            <div className="audit-terminal">
              <header className="audit-header">
                <div className="audit-header-left">
                  <FaShieldAlt className="audit-shield" />
                  <div>
                    <h2 className="audit-title">System Audit Log</h2>
                    <p className="audit-sub">Tamper-evident record of all admin actions — {auditLog.length} entries</p>
                  </div>
                </div>
                <div className="audit-header-right">
                  <span className="audit-status-dot" />
                  <span className="audit-status-label">Live</span>
                  <button
                    className="audit-export-btn"
                    title="Copy audit log"
                    onClick={() => {
                      const lines = auditLog.map((e, i) => {
                        const ts = e.timestamp?.seconds
                          ? new Date(e.timestamp.seconds * 1000).toISOString()
                          : "—";
                        return `[${String(auditLog.length - i).padStart(4, "0")}] ${ts}  ${e.action?.padEnd(28)}  ${e.admin || "—"}  ${e.target || ""}`;
                      });
                      navigator.clipboard.writeText(
                        `THE PP STUDIO — ADMIN AUDIT LOG\nExported: ${new Date().toISOString()}\n${"─".repeat(90)}\n` + lines.join("\n")
                      ).catch(() => {});
                    }}
                  >
                    Export
                  </button>
                </div>
              </header>

              <div className="audit-col-labels">
                <span>#</span>
                <span>Timestamp</span>
                <span>Action</span>
                <span>Description</span>
                <span>Admin</span>
              </div>

              <div className="audit-entries">
                {auditLog.length === 0 ? (
                  <div className="audit-empty">No actions recorded yet. Activity will appear here automatically.</div>
                ) : (
                  auditLog.map((entry, i) => {
                    const ts = entry.timestamp?.seconds
                      ? new Date(entry.timestamp.seconds * 1000)
                      : null;
                    const isoDate = ts ? ts.toISOString().replace("T", " ").slice(0, 19) : "—";
                    const actionMap = {
                      "blog.create":          { label: "Post published",      tone: "create" },
                      "blog.update":          { label: "Post updated",        tone: "update" },
                      "blog.delete":          { label: "Post deleted",        tone: "delete" },
                      "contract_tips.create": { label: "Tip published",       tone: "create" },
                      "contract_tips.draft":  { label: "Tip draft saved",     tone: "update" },
                      "contract_tips.update": { label: "Tip updated",         tone: "update" },
                      "contract_tips.delete": { label: "Tip deleted",         tone: "delete" },
                      "podcast.create":       { label: "Episode published",   tone: "create" },
                      "podcast.update":       { label: "Episode updated",     tone: "update" },
                      "podcast.delete":       { label: "Episode deleted",     tone: "delete" },
                      "comment.approve":      { label: "Comment approved",    tone: "create" },
                      "comment.delete":       { label: "Comment removed",     tone: "delete" },
                    };
                    const { label, tone } = actionMap[entry.action] || { label: entry.action, tone: "neutral" };
                    const seq = String(auditLog.length - i).padStart(4, "0");
                    return (
                      <div key={entry.id} className={`audit-row audit-tone-${tone}`}>
                        <span className="audit-seq">{seq}</span>
                        <span className="audit-ts">{isoDate}</span>
                        <span className={`audit-action-label tone-${tone}`}>{label}</span>
                        <span className="audit-desc" title={entry.target}>{entry.target || "—"}</span>
                        <span className="audit-admin">{entry.admin || "—"}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </section>

        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   TOOLBAR BUTTON
   Commands run in onMouseDown so the editor is
   still focused when the command executes.
   e.preventDefault() keeps editor focus & caret.
═══════════════════════════════════════════════ */
const TB = ({ active, cmd, title, children, danger, className, style, ed }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => {
      e.preventDefault();
      if (ed && !ed.view?.hasFocus()) return;
      cmd?.();
    }}
    className={`adm-tb${active ? " on" : ""}${danger ? " danger" : ""}${className ? ` ${className}` : ""}`}
    style={style}
  >
    {children}
  </button>
);

const NAGA_ACTIONS = [
  { id: "coach", label: "Coach me", hint: "Short editorial next steps" },
  { id: "review", label: "Honest review", hint: "Direct critique of weak spots" },
  { id: "headline", label: "Headline", hint: "Sharper title options" },
  { id: "intro", label: "Intro", hint: "Draft one opening paragraph" },
  { id: "simplify", label: "Simplify", hint: "Rewrite selected text clearly" },
  { id: "seo", label: "SEO", hint: "Meta title and description" },
  { id: "cta", label: "CTA", hint: "Soft closing prompt" },
];

const NagaWritingCompanion = ({ editor, form, section, wordCount, headings, onInsertText, onReplaceTitle }) => {
  const [open, setOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const localHints = useMemo(() => {
    const hints = [];
    if (!form.title.trim()) hints.push("Add a clear working title first.");
    if (wordCount > 250 && headings.length === 0) hints.push("Add one H2 so readers can scan.");
    if (wordCount > 500 && toArr(form.tags).length === 0) hints.push("Add 3-5 tags for search and grouping.");
    if (!form.subtitle.trim() && wordCount > 120) hints.push(section === "blogs" ? "Add a one-line hook for the listing." : "Add a key takeaway sentence.");
    if (wordCount > 900 && !/disclaimer|general information|not legal advice/i.test(editor?.getText?.() || "")) {
      hints.push("Consider a short general-information disclaimer near the end.");
    }
    return hints.slice(0, 3);
  }, [editor, form.subtitle, form.tags, form.title, headings.length, section, wordCount]);

  const selectedText = useCallback(() => {
    if (!editor) return "";
    const { from, to, empty } = editor.state.selection;
    if (empty) return "";
    return editor.state.doc.textBetween(from, to, " ").replace(/\s+/g, " ").trim().slice(0, 1200);
  }, [editor]);

  const nearbyText = useCallback(() => {
    if (!editor) return "";
    const { $from } = editor.state.selection;
    return ($from.parent?.textContent || editor.getText() || "").replace(/\s+/g, " ").trim().slice(0, 1500);
  }, [editor]);

  const askNaga = async (action) => {
    if (!editor || loadingAction) return;
    setOpen(true);
    setError("");
    setResult(null);
    setLoadingAction(action);
    try {
      const token = await auth.currentUser?.getIdToken();
      const html = editor.getHTML();
      const text = plainTextFromHtml(html);
      const res = await fetch("/api/ai/editor-assist", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action,
          section,
          title: form.title,
          subtitle: form.subtitle,
          category: form.category,
          tags: toArr(form.tags),
          selectedText: selectedText(),
          nearbyText: nearbyText(),
          outlineHeadings: headings,
          draftSample: `${text.slice(0, 900)}\n\n${text.slice(Math.max(0, text.length - 900))}`.trim(),
          wordCount,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Naga could not respond.");
      setResult({ ...data, action });
    } catch (err) {
      setError(err.message || "Naga could not respond.");
    } finally {
      setLoadingAction("");
    }
  };

  const useInsert = () => {
    if (!result?.insert_text) return;
    if (result.action === "headline") {
      onReplaceTitle(result.insert_text);
      return;
    }
    onInsertText(result.insert_text);
  };

  return (
    <aside className={`adm-naga-companion${open ? " open" : ""}${loadingAction ? " thinking" : ""}`}>
      <button type="button" className="adm-naga-avatar-btn" onClick={() => setOpen(v => !v)} aria-label="Open Naga writing companion">
        <span className="adm-naga-avatar">
          <span className="adm-naga-face"><i /><i /></span>
        </span>
        {!open && (localHints.length > 0 || result) && <span className="adm-naga-pulse" />}
      </button>

      {open && (
        <div className="adm-naga-panel">
          <div className="adm-naga-head">
            <div>
              <strong>Naga is beside you</strong>
              <span>{loadingAction ? "Thinking only because you asked…" : "Quiet local watch. Groq runs on click."}</span>
            </div>
            <button type="button" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="adm-naga-stats">
            <span>{wordCount} words</span>
            <span>{headings.length} headings</span>
            <span>{toArr(form.tags).length} tags</span>
          </div>

          <div className="adm-naga-local">
            {localHints.length > 0 ? localHints.map(h => <p key={h}>{h}</p>) : <p>This draft has a clean base. Ask me when you want a second pair of eyes.</p>}
          </div>

          <div className="adm-naga-actions">
            {NAGA_ACTIONS.map(action => (
              <button
                key={action.id}
                type="button"
                onClick={() => askNaga(action.id)}
                disabled={!!loadingAction}
                title={action.hint}
              >
                {loadingAction === action.id ? "Thinking…" : action.label}
              </button>
            ))}
          </div>

          {error && <div className="adm-naga-error">{error}</div>}

          {result && (
            <div className="adm-naga-result">
              {result.message && <p className="adm-naga-message">{result.message}</p>}
              {result.suggestions?.length > 0 && (
                <ul>
                  {result.suggestions.map((s, i) => <li key={`${s}-${i}`}>{s}</li>)}
                </ul>
              )}
              {result.insert_text && (
                <div className="adm-naga-insert">
                  <label>Naga draft</label>
                  <p>{result.insert_text}</p>
                  <button type="button" onClick={useInsert}>
                    {result.action === "headline" ? "Use as title" : "Insert into editor"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

/* ═══════════════════════════════════════════════
   WRITING COACH (Pip) — animated avatar companion
═══════════════════════════════════════════════ */
const PIP_GREETINGS = [
  "Hi Pooja! Ready to write something brilliant? ✨",
  "Welcome back, Pooja. I'm watching — make it good. 🌿",
  "Here you are! I've been waiting. Let's create something real. ✍️",
  "Pooja! Your audience needs to hear this. Let's go. 🔥",
  "You showed up — that's already half the battle. Write. 💫",
  "Hi Pooja! Every word you write today will matter to someone. 🌟",
  "Ready when you are. Your voice is one of a kind, Pooja. ✨",
  "Pooja's in the building. Time to say the thing others won't. 🌿",
  "Today's piece could be the one that changes everything. Let's go. ✍️",
  "Hi Pooja! Stop overthinking. Start typing. The rest follows. 💛",
  "Your readers are lucky to have you. Let's write. 🌟",
  "Your perspective is exactly what's missing online, Pooja. 🔥",
  "Blank page, full mind. I'm right here with you. ✨",
  "Welcome, Pooja. Today's writing session is going to feel good. 💫",
  "You sat down to write. That already puts you ahead. Let's go. 🌿",
  "Hi Pooja! The world needs your clarity on this. Don't hold back. ✍️",
  "New session, new magic. What are we creating today, Pooja? 🌟",
];

const WritingCoach = ({ editor, section }) => {
  const [message, setMessage]   = useState(null);
  const [visible, setVisible]   = useState(false);
  const [thinking, setThinking] = useState(false);
  const [reacting, setReacting] = useState(false);

  const lastCallMs    = useRef(0);
  const lastText      = useRef('');
  const thinkingRef   = useRef(false);
  const hideRef       = useRef(null);
  const reactRef      = useRef(null);
  const sectionRef    = useRef(section);
  useEffect(() => { sectionRef.current = section; }, [section]);

  const showMsg = useCallback((msg, duration = 8_000) => {
    setMessage(msg);
    setVisible(true);
    setReacting(true);
    clearTimeout(reactRef.current);
    reactRef.current = setTimeout(() => setReacting(false), 650);
    clearTimeout(hideRef.current);
    hideRef.current = setTimeout(() => setVisible(false), duration);
  }, []);

  /* Warm greeting the moment the editor opens */
  useEffect(() => {
    const greeting = PIP_GREETINGS[Math.floor(Math.random() * PIP_GREETINGS.length)];
    setTimeout(() => showMsg(greeting, 7_000), 800); // tiny delay so it feels natural
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Poll every 3s — no dependency on editor.on('update') which can be unreliable */
  useEffect(() => {
    if (!editor) return;
    const interval = setInterval(async () => {
      if (thinkingRef.current) return;
      const text = editor.getText().trim();
      if (text.length < 50) return;
      if (text === lastText.current) return;       // nothing changed
      const now = Date.now();
      if (now - lastCallMs.current < 10_000) return; // max once per 10s

      lastCallMs.current  = now;
      lastText.current    = text;
      thinkingRef.current = true;
      setThinking(true);

      try {
        const token = await auth.currentUser?.getIdToken();
        const res   = await fetch('/api/ai/writing-coach', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ text: text.slice(-300), type: sectionRef.current }),
        });
        const data = res.ok ? await res.json() : null;
        if (data?.message) showMsg(data.message, 9_000);
      } catch (_) {}

      thinkingRef.current = false;
      setThinking(false);
    }, 3_000);

    return () => {
      clearInterval(interval);
      clearTimeout(hideRef.current);
      clearTimeout(reactRef.current);
    };
  }, [editor, showMsg]);

  return (
    <div className={`wc-root${thinking ? ' wc-thinking' : ''}`} aria-live="polite">
      {visible && message && (
        <div className="wc-bubble">
          <button className="wc-close" onClick={() => setVisible(false)} aria-label="Dismiss">×</button>
          <p className="wc-text">{message}</p>
        </div>
      )}
      <div className={`wc-avatar${reacting ? ' wc-react' : ''}`} title="Pip — your writing strategist">
        {thinking ? <span className="wc-dots"><span/><span/><span/></span> : '✍️'}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   EDITOR
═══════════════════════════════════════════════ */
const PostEditor = ({ section: initSection, editDoc, onBack, onSaved }) => {
  const [section, setSection] = useState(initSection);
  const [saving, setSaving] = useState(false);
  const [audioGenerating, setAudioGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadErr, setUploadErr] = useState("");
  const [toast, setToast] = useState("");
  const [panel, setPanel] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [previewingVer, setPreviewingVer] = useState(null);
  const versionCountRef = useRef(editDoc?.versionCount || 0);
  // Tracks whether THIS doc is published, independent of the (possibly
  // stale) editDoc prop — updated the instant a save publishes it, so
  // autosave can never re-clobber a just-published post back to draft.
  const isPublishedRef = useRef(editDoc?.draft === false);
  const attributionRef = useRef("");
  const [ytOpen, setYtOpen] = useState(false);
  const [ytUrl, setYtUrl] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const coverRef = useRef();
  const imgRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [draggingCover, setDraggingCover] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, px: 50, py: 50 });
  const [, setDraftPulse] = useState(0);

  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const handleSaveRef = useRef(null);
  const formRef       = useRef(null);
  const savingRef     = useRef(false);

  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    tags: "",
    category: "",
    featured: false,
    importance: 10,
    coverImage: "",
    coverPositionX: 50,
    coverPositionY: 50,
    referenceLinks: "",
    authorName: "Pooja Biswal",
    authorRole: "Contract Management Specialist",
    authorBio: "",
    authorAvatar: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /* Populate when editing */
  useEffect(() => {
    if (!editDoc) return;
    setForm({
      title: editDoc.title || "",
      subtitle: editDoc.subtitle || editDoc.excerpt || "",
      tags: tagsToString(editDoc.tags),
      category: editDoc.category || "",
      featured: !!editDoc.featured,
      importance: editDoc.importance ?? 10,
      coverImage: editDoc.coverImage || editDoc.thumbnail || "",
      coverPositionX: editDoc.coverPositionX ?? 50,
      coverPositionY: editDoc.coverPositionY ?? 50,
      referenceLinks: (editDoc.referenceLinks || []).join("\n"),
      authorName: editDoc.author?.name || "Pooja Biswal",
      authorRole: editDoc.author?.role || "Contract Management Specialist",
      authorBio: editDoc.author?.bio || "",
      authorAvatar: editDoc.author?.avatar || "",
    });
  }, [editDoc]);

  /* TipTap */
  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({ link: false, underline: false, heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      Color,
      FontFamily.configure({ types: ['textStyle'] }),
      FontSize,
      Highlight.configure({ multicolor: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      LinkExt.configure({ openOnClick: false }),
      ResizableImage,
      YoutubeExt.configure({ width: "100%", height: 420, nocookie: true }),
      Placeholder.configure({
        placeholder: ({ node }) =>
          node.type.name === "heading"
            ? "Heading…"
            : "Start writing here… Select text to format it.",
      }),
    ],
    content: editDoc?.content || "",
    editorProps: { attributes: { class: "adm-prose" } },
    onUpdate: () => setDraftPulse(v => v + 1),
  });

  /* Auto-select current block when nothing highlighted, then return chain.
     NOTE: do NOT call .focus() inside this chain — focus(null) in TipTap v3
     resets the selection before setColor/setFontFamily can see it. The
     e.preventDefault() on the toolbar button keeps editor focus & selection. */
  const selChain = useCallback(() => {
    if (!editor) return null;
    const { empty, $from } = editor.state.selection;
    const c = editor.chain();
    if (empty) {
      const start = $from.start($from.depth);
      const end   = $from.end($from.depth);
      if (end > start) c.setTextSelection({ from: start, to: end });
    }
    return c;
  }, [editor]);

  /* Cover drag-to-reposition */
  useEffect(() => {
    if (!draggingCover) return;
    const onMove = (e) => {
      const dy = e.clientY - dragStart.current.y;
      const dx = e.clientX - dragStart.current.x;
      const newY = Math.max(0, Math.min(100, dragStart.current.py - dy * 0.15));
      const newX = Math.max(0, Math.min(100, dragStart.current.px - dx * 0.08));
      setForm(f => ({ ...f, coverPositionY: newY, coverPositionX: newX }));
    };
    const onUp = () => setDraggingCover(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
  }, [draggingCover]);

  /* File → Firebase Storage → URL */
  const uploadFile = useCallback((file, folder) => {
    return new Promise((resolve, reject) => {
      const r = ref(storage, `${folder}/${Date.now()}-${file.name}`);
      const task = uploadBytesResumable(r, file);
      task.on(
        "state_changed",
        (s) => setUploadPct(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
        reject,
        async () => { resolve(await getDownloadURL(task.snapshot.ref)); setUploadPct(0); }
      );
    });
  }, []);

  const handleCover = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    setUploadErr("");
    try { set("coverImage", await uploadFile(file, "covers")); }
    catch { setUploadErr("Upload failed — Firebase Storage rules need to allow writes. See setup guide."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleInlineImg = async (e) => {
    const file = e.target.files[0]; if (!file || !editor) return;
    setUploading(true);
    setUploadErr("");
    try {
      const url = await uploadFile(file, "images");
      editor.chain().focus().setImage({ src: url }).run();
    } catch { setUploadErr("Upload failed — Firebase Storage rules need to allow writes."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const insertYt = () => {
    if (!ytUrl || !editor) return;
    editor.chain().focus().setYoutubeVideo({ src: ytUrl }).run();
    setYtUrl(""); setYtOpen(false);
  };

  const insertLink = () => {
    if (!editor) return;
    linkUrl
      ? editor.chain().focus().setLink({ href: linkUrl }).run()
      : editor.chain().focus().unsetLink().run();
    setLinkUrl(""); setLinkOpen(false);
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  /* ── Version history helpers ── */
  const relTime = (ts) => {
    if (!ts) return '';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    const s = Math.floor((Date.now() - d) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const loadVersions = useCallback(async () => {
    const slug = editDoc?.id;
    if (!slug) { showToast("⚠ Save the post at least once before viewing history."); return; }
    setVersionsLoading(true);
    setVersions([]);
    try {
      const snap = await getDocs(
        query(collection(db, section, slug, 'versions'), orderBy('version', 'desc'), limit(60))
      );
      setVersions(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
    } catch (err) {
      console.error('loadVersions:', err);
      showToast("✕ Could not load history — check Firestore rules.");
    } finally {
      setVersionsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDoc, section]);

  const openHistory = () => {
    setHistOpen(true);
    loadVersions();
  };

  const previewVer = (v) => {
    setPreviewingVer(v);
    editor?.commands.setContent(v.content || '');
    setForm(f => ({
      ...f,
      title:      v.title     || f.title,
      subtitle:   v.excerpt   || v.subtitle || f.subtitle,
      coverImage: v.thumbnail || v.coverImage || f.coverImage,
      tags:       tagsToString(v.tags) || f.tags,
    }));
  };

  const dismissPreview = () => {
    setPreviewingVer(null);
    showToast("Dismissed — your current draft is restored on next reload.");
  };

  const handleSave = async (draft = false, exit = false, skipNav = false) => {
    if (!form.title.trim()) { showToast("⚠ Please add a title before saving."); return; }
    setSaving(true);
    try {
      const slug = editDoc?.id || slugify(form.title);
      const isNew = !editDoc?.id;
      const dateField = section === "blogs" ? "createdAt" : "publishedAt";
      const data = {
        title: form.title.trim(),
        content: editor?.getHTML() || "",
        slug,
        tags: toArr(form.tags),
        featured: form.featured,
        thumbnail: form.coverImage,
        coverImage: form.coverImage,
        coverPositionX: form.coverPositionX,
        coverPositionY: form.coverPositionY,
        draft,
        attribution: attributionRef.current || "",
        author: {
          name: form.authorName,
          role: form.authorRole,
          bio: form.authorBio,
          avatar: form.authorAvatar,
        },
        updatedAt: serverTimestamp(),
        ...(section === "blogs"
          ? { excerpt: form.subtitle, views: editDoc?.views || 0, likes: editDoc?.likes || 0 }
          : {
              subtitle: form.subtitle,
              category: form.category,
              importance: Number(form.importance),
              referenceLinks: form.referenceLinks.split("\n").map((s) => s.trim()).filter(Boolean),
            }),
        ...(isNew ? { [dateField]: serverTimestamp() } : {}),
      };
      versionCountRef.current += 1;
      const nextVer = versionCountRef.current;
      await setDoc(doc(db, section, slug), { ...data, versionCount: nextVer }, { merge: true });
      isPublishedRef.current = !draft;

      /* Write version snapshot */
      await addDoc(collection(db, section, slug, 'versions'), {
        version:  nextVer,
        savedAt:  serverTimestamp(),
        action:   draft ? (isNew ? 'created' : 'draft') : 'published',
        title:    data.title,
        content:  data.content,
        thumbnail: data.thumbnail || "",
        excerpt:  data.excerpt || data.subtitle || "",
        tags:     data.tags || [],
        draft,
      });

      logAction(
        draft ? (isNew ? `${section}.create` : `${section}.draft`) : `${section}.publish`,
        data.title,
        slug
      );
      if (skipNav) return;
      if (!draft || exit) {
        onSaved();
      } else {
        showToast("✓ Draft saved — it won't appear publicly until you Publish.");
      }
    } catch (e) {
      console.error(e);
      if (!skipNav) showToast("✕ Save failed — check Firestore write permissions in Firebase Console.");
      throw e;
    } finally { setSaving(false); }
  };

  const handleGenerateAudio = async () => {
    if (!editDoc?.id) { showToast("⚠ Save the post at least once before generating audio."); return; }
    setAudioGenerating(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/article-audio/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ section, docId: editDoc.id }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Audio generation failed');
      showToast(`✓ Audio ready — ${Math.round(data.durationSeconds)}s in Pooja's voice.`);
    } catch (e) {
      console.error(e);
      showToast("✕ Audio generation failed — check the ElevenLabs key and try again.");
    } finally { setAudioGenerating(false); }
  };

  /* ── keep refs in sync so the interval always uses latest state ── */
  useEffect(() => { handleSaveRef.current = handleSave; });
  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => { savingRef.current = saving; }, [saving]);

  /* ── auto-save every 60 s — only new/draft docs, never overwrite published ── */
  useEffect(() => {
    const id = setInterval(async () => {
      if (!formRef.current?.title?.trim() || savingRef.current || isPublishedRef.current) return;
      try {
        await handleSaveRef.current(true, false, true);
        setAutoSavedAt(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      } catch (_) {}
    }, 60000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;

  const thumbId = ytId(ytUrl);
  const editorHtml = editor.getHTML();
  const editorText = plainTextFromHtml(editorHtml);
  const draftWordCount = editorText ? editorText.split(/\s+/).filter(Boolean).length : 0;
  const draftHeadings = headingTextFromHtml(editorHtml);
  const insertNagaText = (text) => {
    if (!text || !editor) return;
    const html = `<p>${String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n+/g, "</p><p>")}</p>`;
    editor.chain().focus().insertContent(html).run();
  };

  return (
    <div className="adm-editor">
      {/* ── TOAST ── */}
      {toast && <div className={`adm-toast${toast.startsWith("✕") ? " err" : toast.startsWith("⚠") ? " warn" : ""}`}>{toast}</div>}

      {/* ── TOP BAR ── */}
      <div className="adm-topbar">
        <button
          className="adm-topbar-back"
          type="button"
          onClick={() => {
            if (form.title.trim()) {
              handleSave(true, true);
            } else {
              onBack();
            }
          }}
        >
          ← Back
        </button>

        <div className="adm-section-toggle">
          <button
            type="button"
            className={`adm-sec-btn${section === "contract_tips" ? " on" : ""}`}
            onClick={() => setSection("contract_tips")}
          >⚖ Contract Tip</button>
          <button
            type="button"
            className={`adm-sec-btn${section === "blogs" ? " on" : ""}`}
            onClick={() => setSection("blogs")}
          >✍ PP Edit Blog</button>
        </div>

        <div className="adm-topbar-right">
          {editDoc?.id && (
            <button type="button" className={`adm-history-btn${histOpen ? " on" : ""}`} onClick={() => histOpen ? setHistOpen(false) : openHistory()}>
              ⏱ History{editDoc.versionCount ? ` v${versionCountRef.current}` : ""}
            </button>
          )}
          <button type="button" className={`adm-settings-btn${panel ? " on" : ""}`} onClick={() => setPanel((o) => !o)}>
            ⚙ Settings
          </button>
          {editDoc?.id && (
            <button type="button" className="adm-history-btn" onClick={handleGenerateAudio} disabled={audioGenerating}>
              {audioGenerating ? "🎧 Generating…" : "🎧 Generate audio"}
            </button>
          )}
          {autoSavedAt && (
            <span className="adm-autosave-badge">✓ Auto-saved {autoSavedAt}</span>
          )}
          <button type="button" className="adm-draft-btn" onClick={() => handleSave(true)} disabled={saving}>
            Save Draft
          </button>
          <button
            type="button"
            className="adm-publish-btn"
            disabled={saving}
            onClick={() => {
              if (!form.title.trim()) { showToast("⚠ Please add a title before publishing."); return; }
              setPublishOpen(true);
            }}
          >
            ✦ Publish
          </button>
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="adm-toolbar">
        <div className="adm-tb-row">

          {/* ── Text style ── */}
          <div className="adm-tb-group">
            <TB active={editor.isActive("bold")}      ed={editor} cmd={() => editor.chain().focus().toggleBold().run()}      title="Bold"><b>B</b></TB>
            <TB active={editor.isActive("italic")}    ed={editor} cmd={() => editor.chain().focus().toggleItalic().run()}    title="Italic"><i>I</i></TB>
            <TB active={editor.isActive("underline")} ed={editor} cmd={() => editor.chain().focus().toggleUnderline().run()} title="Underline"><u>U</u></TB>
            <TB active={editor.isActive("strike")}    ed={editor} cmd={() => editor.chain().focus().toggleStrike().run()}    title="Strikethrough"><s>S</s></TB>
            <TB active={editor.isActive("highlight")} ed={editor} cmd={() => editor.chain().focus().toggleHighlight().run()} title="Highlight">✦ Mark</TB>
          </div>

          <div className="adm-tb-sep" />

          {/* ── Headings ── */}
          <div className="adm-tb-group">
            <TB active={editor.isActive("heading",{level:1})} ed={editor} cmd={() => editor.chain().focus().toggleHeading({level:1}).run()} title="H1 — Large handwriting title" className="adm-tb-h1">H1</TB>
            <TB active={editor.isActive("heading",{level:2})} ed={editor} cmd={() => editor.chain().focus().toggleHeading({level:2}).run()} title="H2 — Section header" className="adm-tb-h2">H2</TB>
            <TB active={editor.isActive("heading",{level:3})} ed={editor} cmd={() => editor.chain().focus().toggleHeading({level:3}).run()} title="H3 — Sub-section" className="adm-tb-h3">H3</TB>
          </div>

          <div className="adm-tb-sep" />

          {/* ── Font family ── */}
          <div className="adm-tb-group adm-tb-fonts">
            <span className="adm-font-label">Aa</span>
            {[
              { value: "'Poppins',sans-serif",   label: "Body",    style: { fontFamily:"'Poppins',sans-serif", fontWeight:600 } },
              { value: "'Caveat Brush',cursive", label: "Script",  style: { fontFamily:"'Caveat Brush',cursive", fontSize:"1.15rem" } },
              { value: "Georgia,serif",          label: "Serif",   style: { fontFamily:"Georgia,serif" } },
              { value: "'Courier New',monospace",label: "Mono",    style: { fontFamily:"'Courier New',monospace", fontSize:"0.82rem" } },
            ].map(({ value, label, style }) => {
              const active = editor.getAttributes("textStyle").fontFamily?.includes(value.split(",")[0].replace(/'/g,""));
              return (
                <button key={value} type="button"
                  className={`adm-tb adm-tb-font${active ? " on" : ""}`}
                  title={label}
                  style={style}
                  onMouseDown={(e) => { e.preventDefault(); selChain()?.setFontFamily(value).run(); }}
                >{label}</button>
              );
            })}
            <button type="button"
              className="adm-tb adm-tb-font"
              title="Default font"
              onMouseDown={(e) => { e.preventDefault(); selChain()?.unsetFontFamily().run(); }}
            >Default</button>
          </div>

          <div className="adm-tb-sep" />

          {/* ── Font size ── */}
          <div className="adm-tb-group adm-tb-sizes">
            {["12px","14px","16px","18px","20px","24px","28px","32px","36px"].map(sz => {
              const active = editor.getAttributes("textStyle").fontSize === sz;
              return (
                <button key={sz} type="button"
                  className={`adm-tb adm-tb-size${active ? " on" : ""}`}
                  title={sz}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    active ? selChain()?.unsetFontSize().run() : selChain()?.setFontSize(sz).run();
                  }}
                >{sz.replace("px","")}</button>
              );
            })}
          </div>

          <div className="adm-tb-sep" />

          {/* ── Colour swatches ── */}
          <div className="adm-tb-group adm-tb-colors">
            <span className="adm-color-label">A</span>
            {[
              { color:"#2C2C2C", label:"Dark"   },
              { color:"#A67C61", label:"Copper" },
              { color:"#7B5EA7", label:"Plum"   },
              { color:"#2d6a4f", label:"Forest" },
              { color:"#1a4e8a", label:"Navy"   },
              { color:"#b5451b", label:"Rust"   },
              { color:"#888888", label:"Grey"   },
              { color:"#E8896A", label:"Peach"  },
            ].map(({ color, label }) => {
              const isActive = editor.getAttributes("textStyle").color === color;
              return (
                <button key={color} type="button"
                  className={`adm-color-swatch${isActive ? " active" : ""}`}
                  style={{ background: color }}
                  title={label}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isActive ? selChain()?.unsetColor().run() : selChain()?.setColor(color).run();
                  }}
                />
              );
            })}
            <button type="button"
              className="adm-color-swatch adm-color-reset"
              title="Remove colour"
              onMouseDown={(e) => { e.preventDefault(); selChain()?.unsetColor().run(); }}
            >✕</button>
          </div>

          <div className="adm-tb-sep" />

          {/* ── Block elements ── */}
          <div className="adm-tb-group">
            <TB active={editor.isActive("bulletList")}  ed={editor} cmd={() => editor.chain().focus().toggleBulletList().run()}  title="Bullet list">• List</TB>
            <TB active={editor.isActive("orderedList")} ed={editor} cmd={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">1. List</TB>
            <TB active={editor.isActive("blockquote")}  ed={editor} cmd={() => editor.chain().focus().toggleBlockquote().run()}  title="Blockquote">" Quote</TB>
            <TB active={false} ed={editor} cmd={() => editor.chain().focus().setHorizontalRule().run()} title="Divider line">― Line</TB>
          </div>

          <div className="adm-tb-sep" />

          {/* ── Insert ── */}
          <div className="adm-tb-group">
            <TB active={editor.isActive("link")} ed={editor} cmd={() => { setLinkUrl(editor.getAttributes("link").href || ""); setLinkOpen(true); }} title="Link">🔗 Link</TB>
            <TB active={false} ed={editor} cmd={() => imgRef.current?.click()} title="Image">🖼 Image</TB>
            <TB active={false} ed={editor} cmd={() => setYtOpen(true)} title="YouTube">▶ Video</TB>
          </div>

          <div className="adm-tb-sep" />
          <TB active={false} ed={editor} cmd={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Remove all formatting" danger>✕ Clear</TB>

        </div>
        <input ref={imgRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handleInlineImg} />
      </div>

      {/* ── BODY ── */}
      <div className={`adm-body${panel ? " has-panel" : ""}`}>

        {/* Main writing area */}
        <div className="adm-main">

          {/* Cover image */}
          <div
            className={`adm-cover${form.coverImage ? " filled" : ""}${uploading ? " uploading" : ""}${dragOver ? " drag-over" : ""}${draggingCover ? " repositioning" : ""}`}
            style={form.coverImage ? {
              backgroundImage: `url(${form.coverImage})`,
              backgroundPosition: `${form.coverPositionX}% ${form.coverPositionY}%`,
            } : {}}
            onMouseDown={form.coverImage ? (e) => {
              if (e.target.tagName === "BUTTON" || e.target.tagName === "INPUT") return;
              setDraggingCover(true);
              dragStart.current = { x: e.clientX, y: e.clientY, px: form.coverPositionX, py: form.coverPositionY };
              e.preventDefault();
            } : undefined}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files[0];
              if (file && file.type.startsWith("image/")) {
                handleCover({ target: { files: [file], value: "" } });
              }
            }}
          >
            {!form.coverImage ? (
              <div className="adm-cover-cta" onClick={() => coverRef.current?.click()}>
                {uploading
                  ? <><span className="adm-cover-icon">⬆</span><strong>Uploading… {uploadPct}%</strong></>
                  : dragOver
                    ? <><span className="adm-cover-icon">⬇</span><strong>Drop to upload</strong></>
                    : <><span className="adm-cover-icon">🖼</span><strong>Add cover image</strong><span>Click to upload or drag &amp; drop</span><em>JPG, PNG, WebP — recommended 1400 × 800 px</em></>
                }
              </div>
            ) : (
              <div className="adm-cover-controls">
                <span className="adm-cover-drag-hint">↕ Drag to reposition</span>
                <button type="button" className="adm-cover-change" onClick={() => coverRef.current?.click()}>
                  Change image
                </button>
                <button type="button" className="adm-cover-remove" onClick={() => set("coverImage", "")}>
                  ✕ Remove
                </button>
              </div>
            )}
            <input ref={coverRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCover} />
          </div>
          {uploadErr && (
            <div className="adm-upload-err">
              ⚠ {uploadErr}
              <button type="button" onClick={() => setUploadErr("")}>✕</button>
            </div>
          )}

          {/* Title */}
          <textarea
            className="adm-title"
            placeholder="Write your title here…"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            rows={2}
          />

          {/* Subtitle / excerpt */}
          <textarea
            className="adm-subtitle"
            placeholder={
              section === "blogs"
                ? "One-line hook — what's this article about? (shown on the blog listing)"
                : "Key takeaway — one sentence that summarises this tip"
            }
            value={form.subtitle}
            onChange={(e) => set("subtitle", e.target.value)}
            rows={2}
          />

          {/* Editor */}
          <EditorContent editor={editor} className="adm-content" />
          <WritingCoach editor={editor} section={section} />
          <NagaWritingCompanion
            editor={editor}
            form={form}
            section={section}
            wordCount={draftWordCount}
            headings={draftHeadings}
            onInsertText={insertNagaText}
            onReplaceTitle={(title) => set("title", title)}
          />
        </div>

        {/* Settings panel */}
        {panel && (
          <aside className="adm-panel-aside">
            <div className="adm-panel-title">
              <h3>Post Settings</h3>
              <button type="button" onClick={() => setPanel(false)}>✕</button>
            </div>

            <div className="adm-field">
              <label>Tags <em>comma separated</em></label>
              <input placeholder="contracts, freelancing, tips" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
            </div>

            {section === "contract_tips" && (
              <>
                <div className="adm-field">
                  <label>Category</label>
                  <input placeholder="e.g. Payment, Scope, IP Rights" value={form.category} onChange={(e) => set("category", e.target.value)} />
                </div>
                <div className="adm-field">
                  <label>Sort Order <em>lower number = shown first</em></label>
                  <input type="number" min={1} value={form.importance} onChange={(e) => set("importance", e.target.value)} />
                </div>
                <div className="adm-field">
                  <label>Reference Links <em>one URL per line</em></label>
                  <textarea rows={3} placeholder={"https://example.com\nhttps://another.com"} value={form.referenceLinks} onChange={(e) => set("referenceLinks", e.target.value)} />
                </div>
              </>
            )}

            {section === "blogs" && (
              <div className="adm-field">
                <label className="adm-toggle-row">
                  <div className="adm-toggle-wrap">
                    <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />
                    <span className="adm-toggle" />
                  </div>
                  <span>Feature in "Most Loved"</span>
                </label>
              </div>
            )}

            <div className="adm-field">
              <label>Cover Image URL <em>or use upload above</em></label>
              <input type="url" placeholder="https://…" value={form.coverImage} onChange={(e) => set("coverImage", e.target.value)} />
            </div>

            <div className="adm-divider" />
            <h4 className="adm-subhead">Author</h4>

            <div className="adm-field">
              <label>Name</label>
              <input value={form.authorName} onChange={(e) => set("authorName", e.target.value)} />
            </div>
            <div className="adm-field">
              <label>Title / Role</label>
              <input value={form.authorRole} onChange={(e) => set("authorRole", e.target.value)} />
            </div>
            <div className="adm-field">
              <label>Short Bio</label>
              <textarea rows={3} placeholder="One or two sentences about Pooja…" value={form.authorBio} onChange={(e) => set("authorBio", e.target.value)} />
            </div>
            <div className="adm-field">
              <label>Avatar Image URL</label>
              <input type="url" placeholder="https://…" value={form.authorAvatar} onChange={(e) => set("authorAvatar", e.target.value)} />
            </div>

            {editDoc && (
              <>
                <div className="adm-divider" />
                <div className="adm-field">
                  <label>URL Slug <em>set when first published</em></label>
                  <input className="adm-slug" value={editDoc.id} readOnly />
                  <span className="adm-slug-path">/{section === "blogs" ? "blog" : "contract-tips"}/{editDoc.id}</span>
                </div>
              </>
            )}
          </aside>
        )}
      </div>

      {/* ── VERSION HISTORY DRAWER ── */}
      {histOpen && (
        <div className="adm-hist-drawer">
          <div className="adm-hist-head">
            <span className="adm-hist-title">⏱ Version History</span>
            <button className="adm-hist-close" onClick={() => setHistOpen(false)}>✕</button>
          </div>

          {previewingVer && (
            <div className="adm-hist-preview-bar">
              <span>Viewing <strong>v{previewingVer.version}</strong> — {previewingVer.action}</span>
              <div className="adm-hist-preview-actions">
                <button className="adm-hist-restore" onClick={() => { handleSave(previewingVer.draft !== false, false, false); setPreviewingVer(null); setHistOpen(false); }}>
                  ✓ Restore this version
                </button>
                <button className="adm-hist-dismiss" onClick={dismissPreview}>Dismiss</button>
              </div>
            </div>
          )}

          <div className="adm-hist-list">
            {versionsLoading && (
              <div className="adm-hist-loading">
                <span className="adm-hist-spinner" />
                Loading history…
              </div>
            )}
            {!versionsLoading && versions.length === 0 && (
              <div className="adm-hist-empty">No versions yet — save or publish to start tracking.</div>
            )}
            {versions.map((v) => (
              <button
                key={v._id}
                type="button"
                className={`adm-hist-item${previewingVer?._id === v._id ? ' active' : ''}`}
                onClick={() => previewVer(v)}
              >
                <div className="adm-hist-item-top">
                  <span className="adm-hist-ver">v{v.version}</span>
                  <span className={`adm-hist-badge adm-hist-badge--${v.action}`}>{v.action}</span>
                  <span className="adm-hist-when">{relTime(v.savedAt)}</span>
                </div>
                <div className="adm-hist-item-title">{v.title || 'Untitled'}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── YOUTUBE MODAL ── */}
      {ytOpen && (
        <div className="adm-overlay" onClick={() => setYtOpen(false)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>▶ Embed YouTube Video</h3>
            <p>Paste the link to any YouTube video. It'll appear right inside your article.</p>
            <input
              className="adm-modal-input"
              type="url"
              placeholder="https://www.youtube.com/watch?v=…"
              value={ytUrl}
              onChange={(e) => setYtUrl(e.target.value)}
              autoFocus
            />
            {thumbId && (
              <div className="adm-yt-preview">
                <img
                  src={`https://img.youtube.com/vi/${thumbId}/hqdefault.jpg`}
                  alt="Video thumbnail"
                />
                <span className="adm-yt-play">▶</span>
              </div>
            )}
            <div className="adm-modal-btns">
              <button type="button" className="adm-modal-cancel" onClick={() => { setYtUrl(""); setYtOpen(false); }}>Cancel</button>
              <button type="button" className="adm-modal-ok" onClick={insertYt} disabled={!ytUrl}>Embed Video</button>
            </div>
          </div>
        </div>
      )}

      {/* ── LINK MODAL ── */}
      {linkOpen && (
        <div className="adm-overlay" onClick={() => setLinkOpen(false)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🔗 Add Link</h3>
            <p>Paste the URL you want to link to. Select text first to create a hyperlink.</p>
            <input
              className="adm-modal-input"
              type="url"
              placeholder="https://…"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              autoFocus
            />
            <div className="adm-modal-btns">
              <button type="button" className="adm-modal-cancel" onClick={() => { editor.chain().focus().unsetLink().run(); setLinkOpen(false); }}>
                Remove Link
              </button>
              <button type="button" className="adm-modal-ok" onClick={insertLink}>Add Link</button>
            </div>
          </div>
        </div>
      )}

      {/* ── PUBLISH FLOW MODAL ── */}
      {publishOpen && (
        <PublishFlowModal
          form={form}
          section={section}
          slug={editDoc?.id || slugify(form.title)}
          onPublish={async (attribution) => {
            attributionRef.current = attribution;
            await handleSave(false, false, true);
          }}
          onClose={() => { setPublishOpen(false); onSaved(); }}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   ROOT
═══════════════════════════════════════════════ */
const Admin = () => {
  const [user, setUser] = useState(undefined); // undefined = still checking
  const [view, setView] = useState("dashboard");
  const [editSection, setEditSection] = useState("contract_tips");
  const [editDoc, setEditDoc] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return unsub;
  }, []);

  const goNew = (section, prefill = null) => { setEditSection(section); setEditDoc(prefill); setView("editor"); };
  const goEdit = (section, d) => { setEditSection(section); setEditDoc(d); setView("editor"); };
  const goBack = () => { setView("dashboard"); setEditDoc(null); };
  const goSaved = () => { setView("dashboard"); setEditDoc(null); };
  const logout = () => signOut(auth);

  /* Still checking Firebase auth state */
  if (user === undefined) {
    return (
      <div className="adm-login">
        <SEO title="Admin" description="Private admin area for The PP Studio." canonical="/admin" noIndex />
        <div className="adm-login-card">
          <div className="adm-spinner" style={{ margin: "0 auto" }} />
        </div>
      </div>
    );
  }

  if (!user) return <><SEO title="Admin" description="Private admin area for The PP Studio." canonical="/admin" noIndex /><LoginScreen /></>;
  if (view === "editor")
    return <><SEO title="Admin Editor" description="Private admin editor for The PP Studio." canonical="/admin" noIndex /><PostEditor section={editSection} editDoc={editDoc} onBack={goBack} onSaved={goSaved} /></>;
  return <><SEO title="Admin Dashboard" description="Private admin dashboard for The PP Studio." canonical="/admin" noIndex /><Dashboard onNew={goNew} onEdit={goEdit} onLogout={logout} /></>;
};

export default Admin;
